import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useToast } from '../context/ToastContext';
import { usePlan, LIMITES } from '../context/PlanContext';
import IntroCarousel from '../components/IntroCarousel';
import { Building2, BookOpen, MessageCircle, Crown, Lock, X } from 'lucide-react';

const SLIDES_CLUBES = [
  { Icon: Building2, titulo: 'Bem-vindo aos Clubes', descricao: 'Crie ou participe de clubes de leitura com pessoas que têm os mesmos gostos que você.' },
  { Icon: BookOpen,  titulo: 'Compartilhe seu acervo', descricao: 'Adicione livros seus ao clube para que os membros possam ver e pedir emprestado.' },
  { Icon: MessageCircle, titulo: 'Leia em comunidade', descricao: 'Combine leituras, troque impressões e cresça como leitor junto com outros.' },
];

async function gerarSlugUnico(base) {
  const slugBase = base.trim().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'clube';
  let slug = slugBase;
  let n = 1;
  const MAX = 10;
  while (n <= MAX) {
    const { data } = await supabase.from('clubes').select('id').eq('slug', slug).maybeSingle();
    if (!data) return slug;
    slug = `${slugBase}-${n++}`;
  }
  throw new Error('Não foi possível gerar um link único. Tente novamente.');
}

const CLUB_GRADIENTS = [
  ['#6E50F0', '#3F27B0'],
  ['#FF7A59', '#FF5C8A'],
  ['#356066', '#1F8A5B'],
  ['#3B7DD8', '#1E4A9A'],
  ['#E0762E', '#C54E1A'],
];
const SPINE_OPACITIES = ['0.92', '0.58', '0.78', '0.48', '0.68', '0.84'];

function getClubGradient(id) {
  if (!id) return CLUB_GRADIENTS[0];
  const idx = [...id].reduce((acc, c) => acc + c.charCodeAt(0), 0) % CLUB_GRADIENTS.length;
  return CLUB_GRADIENTS[idx];
}

function ClubBanner({ clubeId, nome, fotoUrl, height = 76, children }) {
  const [g1, g2] = getClubGradient(clubeId);
  return (
    <div className="relative flex items-end overflow-hidden"
      style={{height, background: `linear-gradient(120deg, ${g1}, ${g2})`, padding:'12px 14px'}}>
      {fotoUrl
        ? <img src={fotoUrl} alt="" style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',opacity:0.25}} />
        : <div className="absolute right-3 top-3.5 flex gap-[3px] opacity-80">
            {[44, 54, 36, 48, 40].map((h, i) => (
              <i key={i} className="w-2 rounded-t-[2px] not-italic block flex-shrink-0"
                style={{height: h, background: `rgba(255,255,255,${SPINE_OPACITIES[i]})`}} />
            ))}
          </div>}
      {fotoUrl && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full overflow-hidden border-2 border-white/40 flex-shrink-0">
          <img src={fotoUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
        </div>
      )}
      {children}
    </div>
  );
}

async function carregarPerfis(userIds) {
  if (!userIds.length) return {};
  const { data } = await supabase.from('perfis').select('id, nome, username, foto_url').in('id', userIds);
  const map = {};
  (data || []).forEach(p => { map[p.id] = p; });
  return map;
}

function Avatar({ nome, size = 8 }) {
  return (
    <div className={`w-${size} h-${size} rounded-full bg-paper-2 flex items-center justify-center text-sm font-medium text-ink-500 flex-shrink-0`}>
      {(nome || '?').charAt(0).toUpperCase()}
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
      className="relative w-11 h-6 rounded-full flex-shrink-0 border-0 cursor-pointer outline-none"
      style={{background: value ? '#5B3CE4' : '#D4CFE2', transition:'background 0.2s', WebkitTapHighlightColor:'transparent'}}>
      <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm"
        style={{left: value ? '22px' : '2px', transition:'left 0.2s'}} />
    </button>
  );
}

function getAvatarColor(nome) {
  const colors = ['#6E50F0', '#FF7A59', '#356066', '#3B7DD8', '#E0762E'];
  if (!nome) return colors[0];
  return colors[nome.charCodeAt(0) % colors.length];
}

function MemberBubbles({ membros = [] }) {
  const shown = membros.slice(0, 3);
  const extra = membros.length - shown.length;
  if (!membros.length) return null;
  return (
    <div style={{display:'flex', alignItems:'center'}}>
      {shown.map((m, i) => (
        <div key={i} style={{
          width:22, height:22, borderRadius:'50%', border:'2px solid #fff',
          marginLeft: i === 0 ? 0 : -7,
          background: m.foto_url ? '#ccc' : getAvatarColor(m.nome),
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:9, fontWeight:800, color:'#fff', flexShrink:0,
          overflow:'hidden', position:'relative', zIndex: shown.length - i,
        }}>
          {m.foto_url
            ? <img src={m.foto_url} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}} />
            : (m.nome || '?').charAt(0).toUpperCase()
          }
        </div>
      ))}
      {extra > 0 && (
        <div style={{
          width:22, height:22, borderRadius:'50%', border:'2px solid #fff',
          marginLeft:-7, background:'#EDE8FC', color:'#5B3CE4',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:9, fontWeight:800, flexShrink:0, position:'relative', zIndex:0,
        }}>+{extra}</div>
      )}
    </div>
  );
}

function sortClubes(list, key) {
  if (key !== 'nome') return list;
  return [...list].sort((a, b) => {
    const nA = (a.nome || a.clubes?.nome || '').toLowerCase();
    const nB = (b.nome || b.clubes?.nome || '').toLowerCase();
    return nA.localeCompare(nB, 'pt');
  });
}

function tempoRelativo(ts) {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  const d = Math.floor(diff / 86400);
  return d < 30 ? `${d}d` : new Date(ts).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
}

function Clubes({ onVoltar, clubeIdParaAbrir, setTelaApp }) {
  const { showToast } = useToast();
  const { isPago, openUpgrade } = usePlan();
  const navigate = useNavigate();

  const [userId, setUserId] = useState(null);
  const [mostrarIntro, setMostrarIntro] = useState(false);
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [emailUsuario, setEmailUsuario] = useState('');
  const [tela, setTela] = useState('lista');

  // solicitação de empréstimo dentro do clube
  const [modalEmprestimo, setModalEmprestimo] = useState(null); // livro do clube
  const [prazoEmprestimo, setPrazoEmprestimo] = useState(14);
  const [mensagemEmprestimo, setMensagemEmprestimo] = useState('');
  const [enviandoEmprestimo, setEnviandoEmprestimo] = useState(false);
  const [sucessoEmprestimo, setSucessoEmprestimo] = useState(false);
  const [entradas, setEntradas] = useState([]);
  const [carregando, setCarregando] = useState(true);

  // descobrir
  const [abaLista, setAbaLista] = useState('meus');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  const [clubesPublicos, setClubesPublicos] = useState([]);
  const [, setAmigosNoClube] = useState({});
  const [carregandoPublicos, setCarregandoPublicos] = useState(false);
  const [visualizacao, setVisualizacao] = useState(() => localStorage.getItem('clubes_visualizacao') || 'lista');
  const [ordenacao, setOrdenacao] = useState('recente');
  const [menuOrdem, setMenuOrdem] = useState(false);
  const [membrosMap, setMembrosMap] = useState({});
  const [criadoresMap, setCriadoresMap] = useState({});
  const [desktopAba, setDesktopAba] = useState('estante');

  // criar
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [privado, setPrivado] = useState(false);
  const [corIndex, setCorIndex] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState('');
  const [novaCapaFile, setNovaCapaFile] = useState(null);
  const [novaCapaPreview, setNovaCapaPreview] = useState(null);
  const novaCapaInputRef = useRef(null);
  const [novaFotoFile, setNovaFotoFile] = useState(null);
  const [novaFotoPreview, setNovaFotoPreview] = useState(null);
  const novaFotoInputRef = useRef(null);

  // detalhe
  const [clubeAtivo, setClubeAtivo] = useState(null);
  const [membros, setMembros] = useState([]);
  const [livrosClube, setLivrosClube] = useState([]);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [membroMenuId, setMembroMenuId] = useState(null);
  const [modalConvidar, setModalConvidar] = useState(false);
  const [amigosConvidar, setAmigosConvidar] = useState([]);
  const [selecionadosConvidar, setSelecionadosConvidar] = useState(new Set());
  const [enviandoConvite, setEnviandoConvite] = useState(false);
  const [carregandoAmigos, setCarregandoAmigos] = useState(false);
  const [modalCriarAberto, setModalCriarAberto] = useState(false);

  // contribuição de livros
  const [modalContribuicao, setModalContribuicao] = useState(false);
  const [etapaContribuicao, setEtapaContribuicao] = useState('opcao');
  const [livrosMembro, setLivrosMembro] = useState([]);
  const [livrosSelecionados, setLivrosSelecionados] = useState(new Set());
  const [salvandoContribuicao, setSalvandoContribuicao] = useState(false);

  // configurações (admin)
  const [editNome, setEditNome] = useState('');
  const [editDescricao, setEditDescricao] = useState('');
  const [editPublico, setEditPublico] = useState(true);
  const [editCapa, setEditCapa] = useState(null);
  const [capaPreview, setCapaPreview] = useState(null);
  const [salvandoConfig, setSalvandoConfig] = useState(false);
  const capaInputRef = useRef(null);
  const [confirmandoDelete, setConfirmandoDelete] = useState(false);
  const [modalSairFounder, setModalSairFounder] = useState(false);
  const [feedAtividade, setFeedAtividade] = useState([]);
  const [modalAviso, setModalAviso] = useState(false);
  const [textoAviso, setTextoAviso] = useState('');
  const [enviandoAviso, setEnviandoAviso] = useState(false);

  useEffect(() => {
    if (userId && !localStorage.getItem(`tour_clubes_${userId}`)) {
      setMostrarIntro(true);
    }
  }, [userId]);

  useEffect(() => {
    async function init() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        setNomeUsuario(user.user_metadata?.nome || user.email?.split('@')[0] || '');
        setEmailUsuario(user.email || '');
        const dados = await buscarClubes(user.id);
        setCarregando(false);
        buscarClubesPublicos(user.id, dados);
        const alvo = clubeIdParaAbrir
          ? (dados || []).find(e => e.clube_id === clubeIdParaAbrir)
          : (dados || []).find(e => e.status === 'aprovado' && !e.livros_opcao);
        if (alvo) abrirDetalhe(alvo);
      } else {
        setCarregando(false);
      }
    }
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Abre modal de contribuição quando o detalhe carregou e o membro ainda não contribuiu
  useEffect(() => {
    if (tela === 'detalhe' && clubeAtivo && !carregandoDetalhe) {
      const entrada = entradas.find(e => e.id === clubeAtivo.membroEntradaId);
      if (entrada && entrada.status === 'aprovado' && !entrada.livros_opcao && !modalContribuicao) {
        setModalContribuicao(true);
        setEtapaContribuicao('opcao');
        setLivrosSelecionados(new Set());
      }
    }
  }, [tela, clubeAtivo?.membroEntradaId, carregandoDetalhe]); // eslint-disable-line react-hooks/exhaustive-deps

  async function buscarMembrosClubs(clubeIds) {
    if (!clubeIds?.length) return;
    const { data } = await supabase
      .from('clube_membros')
      .select('clube_id, user_id')
      .in('clube_id', clubeIds)
      .eq('status', 'aprovado');
    if (!data?.length) return;
    const rawMap = {};
    const allUserIds = new Set();
    data.forEach(m => {
      if (!rawMap[m.clube_id]) rawMap[m.clube_id] = [];
      rawMap[m.clube_id].push(m.user_id);
      allUserIds.add(m.user_id);
    });
    const perfisM = await carregarPerfis([...allUserIds]);
    const finalMap = {};
    Object.entries(rawMap).forEach(([cId, uIds]) => {
      finalMap[cId] = uIds.map(uid => perfisM[uid] || { id: uid, nome: null, foto_url: null });
    });
    setMembrosMap(prev => ({ ...prev, ...finalMap }));
  }

  async function buscarCriadores(criadorIds) {
    const ids = [...new Set(criadorIds.filter(Boolean))];
    if (!ids.length) return;
    const pm = await carregarPerfis(ids);
    setCriadoresMap(prev => ({ ...prev, ...pm }));
  }

  async function buscarClubes(uid) {
    let { data, error } = await supabase
      .from('clube_membros')
      .select('id, status, role, livros_opcao, clube_id, clubes(id, nome, descricao, slug, criador_id, capa_url, foto_url, publico)')
      .eq('user_id', uid || userId)
      .order('created_at', { ascending: false });
    if (error) {
      // Fallback sem colunas novas (antes das migrations)
      ({ data } = await supabase
        .from('clube_membros')
        .select('id, status, role, livros_opcao, clube_id, clubes(id, nome, descricao, slug, criador_id)')
        .eq('user_id', uid || userId)
        .order('created_at', { ascending: false }));
    }
    setEntradas(data || []);
    const criadorIds = (data || []).map(e => e.clubes?.criador_id).filter(Boolean);
    buscarCriadores(criadorIds);
    buscarMembrosClubs((data || []).map(e => e.clube_id).filter(Boolean));
    return data;
  }

  async function buscarClubesPublicos(uid, minhasEntradas) {
    setCarregandoPublicos(true);
    try {
      const meusIds = (minhasEntradas || []).map(e => e.clube_id).filter(Boolean);

      const buildQuery = (comCapa) => {
        let q = supabase
          .from('clubes')
          .select(comCapa ? 'id, nome, descricao, slug, capa_url, foto_url, publico' : 'id, nome, descricao, slug, publico')
          .eq('publico', true)
          .limit(100);
        if (meusIds.length > 0) q = q.not('id', 'in', `(${meusIds.join(',')})`);
        return q;
      };

      let { data: publicos, error: errPublicos } = await buildQuery(true);
      if (errPublicos) ({ data: publicos, error: errPublicos } = await buildQuery(false));

      if (errPublicos) {
        setClubesPublicos([]);
        setCarregandoPublicos(false);
        return;
      }
      if (!publicos?.length) {
        setClubesPublicos([]);
        setCarregandoPublicos(false);
        return;
      }

      // amigos que sigo
      const { data: seguindo } = await supabase.from('seguidores').select('seguido_id').eq('seguidor_id', uid);
      const seguindoIds = (seguindo || []).map(s => s.seguido_id);

      let amigoMap = {};
      if (seguindoIds.length > 0) {
        const { data: membrosAmigos } = await supabase.from('clube_membros')
          .select('clube_id, user_id').in('clube_id', publicos.map(c => c.id)).in('user_id', seguindoIds).eq('status', 'aprovado');
        (membrosAmigos || []).forEach(({ clube_id, user_id }) => {
          if (!amigoMap[clube_id]) amigoMap[clube_id] = [];
          amigoMap[clube_id].push(user_id);
        });
      }
      setAmigosNoClube(amigoMap);

      const sorted = [...publicos].sort((a, b) => (amigoMap[b.id]?.length || 0) - (amigoMap[a.id]?.length || 0));
      setClubesPublicos(sorted);
      buscarCriadores((sorted || []).map(c => c.criador_id).filter(Boolean));
      buscarMembrosClubs((sorted || []).map(c => c.id).filter(Boolean));
    } catch (e) {
      setClubesPublicos([]);
    } finally {
      setCarregandoPublicos(false);
    }
  }

  async function handleCriarClube() {
    if (!nome.trim()) { setErroForm('Nome é obrigatório.'); return; }
    setErroForm('');
    setSalvando(true);
    if (!isPago) {
      const { count } = await supabase.from('clube_membros').select('*', { count: 'exact', head: true })
        .eq('user_id', userId).eq('status', 'aprovado');
      if (count >= LIMITES.clubes) { setSalvando(false); openUpgrade('clubes'); return; }
    }
    let slug;
    try { slug = await gerarSlugUnico(nome); }
    catch (e) { setSalvando(false); showToast(e.message, 'error'); return; }
    let novaCapa_url = null;
    if (novaCapaFile) {
      const ext = novaCapaFile.name.split('.').pop();
      const path = `clube-capa-${Date.now()}.${ext}`;
      const { data: upData, error: upErr } = await supabase.storage.from('capas').upload(path, novaCapaFile, {
        upsert: true, contentType: novaCapaFile.type || `image/${ext}`,
      });
      if (!upErr && upData) {
        const { data: urlData } = supabase.storage.from('capas').getPublicUrl(upData.path);
        novaCapa_url = urlData.publicUrl;
      }
    }
    let novaFoto_url = null;
    if (novaFotoFile) {
      const ext = novaFotoFile.name.split('.').pop();
      const path = `clube-foto-${Date.now()}.${ext}`;
      const { data: upData, error: upErr } = await supabase.storage.from('capas').upload(path, novaFotoFile, {
        upsert: true, contentType: novaFotoFile.type || `image/${ext}`,
      });
      if (!upErr && upData) {
        const { data: urlData } = supabase.storage.from('capas').getPublicUrl(upData.path);
        novaFoto_url = urlData.publicUrl;
      }
    }
    const { data: clube, error } = await supabase.from('clubes').insert({
      nome: nome.trim(), descricao: descricao.trim() || null, criador_id: userId, slug, publico: !privado, cor_index: corIndex,
      capa_url: novaCapa_url, foto_url: novaFoto_url,
    }).select().single();
    if (error) { setSalvando(false); showToast('Erro ao criar clube.', 'error'); return; }
    await supabase.from('clube_membros').insert({
      clube_id: clube.id, user_id: userId, status: 'aprovado', role: 'admin',
    });
    // Notificar seguidores sobre o novo clube
    const { data: followers } = await supabase.from('seguidores').select('seguidor_id').eq('seguido_id', userId);
    if (followers?.length) {
      await supabase.from('notificacoes').insert(
        followers.map(f => ({
          user_id: f.seguidor_id, tipo: 'novo_clube_amigo', lida: false,
          meta: { clube_nome: clube.nome, criador_nome: nomeUsuario || 'Alguém', clube_id: clube.id },
        }))
      );
    }
    showToast('Clube criado!', 'success');
    setNome(''); setDescricao(''); setPrivado(false); setCorIndex(0); setNovaCapaFile(null); setNovaCapaPreview(null); setNovaFotoFile(null); setNovaFotoPreview(null);
    const todasEntradas = await buscarClubes();
    setSalvando(false);
    const novaEntrada = (todasEntradas || []).find(e => e.clube_id === clube.id);
    setModalCriarAberto(false);
    if (novaEntrada) {
      if (window.innerWidth >= 1024) {
        setTela('lista');
        selecionarClubeDesktop(novaEntrada);
      } else {
        abrirDetalhe(novaEntrada);
      }
    } else {
      setTela('lista');
    }
  }

  async function selecionarClubeDesktop(entrada) {
    const clube = entrada.clubes;
    if (!clube) return;
    setClubeAtivo({ ...clube, meuRole: entrada.role, meuStatus: entrada.status, minhaOpcao: entrada.livros_opcao || 'nenhum', membroEntradaId: entrada.id });
    setCarregandoDetalhe(true);
    await recarregarDetalhe(clube.id);
    setCarregandoDetalhe(false);
  }

  async function abrirDetalhe(entrada) {
    const clube = entrada.clubes;
    setClubeAtivo({
      ...clube,
      meuRole: entrada.role,
      meuStatus: entrada.status,
      minhaOpcao: entrada.livros_opcao || 'nenhum',
      membroEntradaId: entrada.id,
    });
    setTela('detalhe');
    setDesktopAba('estante');
    setMembroMenuId(null);
    setCarregandoDetalhe(true);
    await recarregarDetalhe(clube.id);
    setCarregandoDetalhe(false);
  }

  async function recarregarDetalhe(clubeId) {
    const [{ data: membrosRaw }, { data: livrosRaw }] = await Promise.all([
      supabase.from('clube_membros').select('id, status, role, user_id, created_at').eq('clube_id', clubeId).order('created_at'),
      supabase.from('clube_livros').select('id, user_id, created_at, livros(id, titulo, autor, estrelas, disponivel, capa_url)').eq('clube_id', clubeId),
    ]);
    const allUserIds = [...new Set([
      ...(membrosRaw || []).map(m => m.user_id),
      ...(livrosRaw || []).map(l => l.user_id),
    ].filter(Boolean))];
    const perfisMap = await carregarPerfis(allUserIds);
    setMembros((membrosRaw || []).map(m => ({ ...m, perfil: perfisMap[m.user_id] || null })));
    setLivrosClube(livrosRaw || []);
    const evtMembros = (membrosRaw || [])
      .filter(m => m.status === 'aprovado')
      .map(m => ({ tipo: 'entrou', nome: perfisMap[m.user_id]?.nome || 'Alguém', ts: m.created_at }));
    const evtLivros = (livrosRaw || [])
      .map(l => ({ tipo: 'livro', nome: perfisMap[l.user_id]?.nome || 'Alguém', livro: l.livros?.titulo, ts: l.created_at }));
    setFeedAtividade([...evtMembros, ...evtLivros].sort((a, b) => new Date(b.ts) - new Date(a.ts)).slice(0, 8));
  }

  const [editFoto, setEditFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const fotoInputRef = useRef(null);

  function abrirConfiguracoes() {
    setEditNome(clubeAtivo.nome || '');
    setEditDescricao(clubeAtivo.descricao || '');
    setEditPublico(clubeAtivo.publico !== false);
    setEditCapa(null);
    setCapaPreview(null);
    setEditFoto(null);
    setFotoPreview(null);
    setConfirmandoDelete(false);
    setTela('configuracoes');
  }

  async function handleSalvarConfig() {
    if (!editNome.trim()) return;
    setSalvandoConfig(true);
    let capa_url = clubeAtivo.capa_url || null;
    if (editCapa) {
      const ext = editCapa.name.split('.').pop();
      const path = `clubes/${clubeAtivo.id}/${Date.now()}.${ext}`;
      const { data, error } = await supabase.storage.from('capas').upload(path, editCapa, {
        upsert: true,
        contentType: editCapa.type || `image/${ext}`,
      });
      if (error) {
        showToast('Erro ao fazer upload da capa: ' + error.message, 'error');
        setSalvandoConfig(false);
        return;
      }
      const { data: urlData } = supabase.storage.from('capas').getPublicUrl(data.path);
      capa_url = urlData.publicUrl;
    }
    let foto_url = clubeAtivo.foto_url || null;
    if (editFoto) {
      const ext = editFoto.name.split('.').pop();
      const path = `clubes/${clubeAtivo.id}/foto-${Date.now()}.${ext}`;
      const { data: fdData, error: fdErr } = await supabase.storage.from('capas').upload(path, editFoto, {
        upsert: true, contentType: editFoto.type || `image/${ext}`,
      });
      if (!fdErr && fdData) {
        const { data: fuData } = supabase.storage.from('capas').getPublicUrl(fdData.path);
        foto_url = fuData.publicUrl;
      }
    }
    await supabase.from('clubes').update({
      nome: editNome.trim(),
      descricao: editDescricao.trim() || null,
      publico: editPublico,
      capa_url,
      foto_url,
    }).eq('id', clubeAtivo.id);
    setClubeAtivo(prev => ({ ...prev, nome: editNome.trim(), descricao: editDescricao.trim() || null, publico: editPublico, capa_url, foto_url }));
    await buscarClubes();
    setSalvandoConfig(false);
    showToast('Clube atualizado!', 'success');
    setTela('detalhe');
  }

  async function handleDeletarClube() {
    await supabase.from('clube_livros').delete().eq('clube_id', clubeAtivo.id);
    await supabase.from('clube_membros').delete().eq('clube_id', clubeAtivo.id);
    await supabase.from('clubes').delete().eq('id', clubeAtivo.id);
    showToast('Clube excluído.', 'info');
    await buscarClubes();
    setClubeAtivo(null);
    setTela('lista');
  }

  async function handleAprovar(membroId) {
    const membro = membros.find(m => m.id === membroId);
    const { error } = await supabase.from('clube_membros').update({ status: 'aprovado' }).eq('id', membroId);
    if (error) {
      showToast('Erro ao aprovar membro. Tente novamente.', 'error');
      return;
    }
    if (membro?.user_id) {
      await supabase.from('notificacoes').insert({
        user_id: membro.user_id,
        tipo: 'clube_aprovado',
        lida: false,
        meta: { clube_nome: clubeAtivo?.nome, clube_id: clubeAtivo?.id },
      });
    }
    if (clubeAtivo?.id) await recarregarDetalhe(clubeAtivo.id);
    showToast('Membro aprovado!', 'success');
  }

  async function handleRejeitar(membroId) {
    const { error } = await supabase.from('clube_membros').update({ status: 'rejeitado' }).eq('id', membroId);
    if (error) {
      showToast('Erro ao recusar solicitação. Tente novamente.', 'error');
      return;
    }
    setMembros(prev => prev.map(m => m.id === membroId ? { ...m, status: 'rejeitado' } : m));
    showToast('Solicitação recusada.', 'info');
  }

  async function handlePromoverAdmin(membroId) {
    await supabase.from('clube_membros').update({ role: 'admin' }).eq('id', membroId);
    setMembros(prev => prev.map(m => m.id === membroId ? { ...m, role: 'admin' } : m));
    setMembroMenuId(null);
    showToast('Membro promovido a admin!', 'success');
  }

  async function handleDemoverAdmin(membroId) {
    await supabase.from('clube_membros').update({ role: 'membro' }).eq('id', membroId);
    setMembros(prev => prev.map(m => m.id === membroId ? { ...m, role: 'membro' } : m));
    setMembroMenuId(null);
    showToast('Admin rebaixado a membro.', 'info');
  }

  async function handleRemoverMembro(membroId) {
    await supabase.from('clube_membros').delete().eq('id', membroId);
    setMembros(prev => prev.filter(m => m.id !== membroId));
    setMembroMenuId(null);
    showToast('Membro removido. Pode solicitar entrada novamente.', 'info');
  }

  async function handleBloquearMembro(membroId) {
    await supabase.from('clube_membros').update({ status: 'bloqueado' }).eq('id', membroId);
    setMembros(prev => prev.map(m => m.id === membroId ? { ...m, status: 'bloqueado' } : m));
    setMembroMenuId(null);
    showToast('Membro bloqueado.', 'info');
  }

  async function handleDesbloquear(membroId) {
    await supabase.from('clube_membros').delete().eq('id', membroId);
    setMembros(prev => prev.filter(m => m.id !== membroId));
    showToast('Membro desbloqueado. Pode solicitar entrada novamente.', 'success');
  }

  async function handleSairDoClube() {
    const clubeId = clubeAtivo.id;
    const entradaId = clubeAtivo.membroEntradaId;
    setClubeAtivo(null);
    setTela('lista');
    setEntradas(prev => prev.filter(e => e.clube_id !== clubeId));
    // Tenta deletar por ambas as chaves — uma delas deve passar pela RLS
    await supabase.from('clube_membros').delete().eq('id', entradaId);
    await supabase.from('clube_membros').delete().eq('clube_id', clubeId).eq('user_id', userId);
    showToast('Você saiu do clube.', 'info');
  }

  async function handleSairFounder() {
    const candidatos = membros.filter(m => m.status === 'aprovado' && m.user_id !== userId);
    const proximoAdmin = candidatos.find(m => m.role === 'admin');
    const novoFounder = proximoAdmin || candidatos[0];

    if (novoFounder) {
      if (!proximoAdmin) {
        await supabase.from('clube_membros').update({ role: 'admin' }).eq('id', novoFounder.id);
      }
      await supabase.from('clubes').update({ criador_id: novoFounder.user_id }).eq('id', clubeAtivo.id);
    }

    const clubeId = clubeAtivo.id;
    const entradaId = clubeAtivo.membroEntradaId;
    setModalSairFounder(false);
    setClubeAtivo(null);
    setTela('lista');
    setEntradas(prev => prev.filter(e => e.clube_id !== clubeId));
    await supabase.from('clube_membros').delete().eq('id', entradaId);
    await supabase.from('clube_membros').delete().eq('clube_id', clubeId).eq('user_id', userId);
    showToast(novoFounder ? 'Você saiu. Propriedade transferida.' : 'Você saiu do clube.', 'info');
  }

  async function handleRemoverLivro(clubeLivroId) {
    await supabase.from('clube_livros').delete().eq('id', clubeLivroId);
    setLivrosClube(prev => prev.filter(l => l.id !== clubeLivroId));
    showToast('Livro removido do clube.', 'info');
  }

  async function carregarLivrosMembro() {
    const { data } = await supabase.from('livros').select('id, titulo, autor, capa_url').eq('user_id', userId).order('titulo');
    setLivrosMembro(data || []);
  }

  async function handleSolicitarEmprestimoClube() {
    if (!modalEmprestimo || !userId) return;
    setEnviandoEmprestimo(true);
    const { data: meuPerfil } = await supabase.from('perfis').select('emprestimo_bloqueado').eq('id', userId).maybeSingle();
    if (meuPerfil?.emprestimo_bloqueado) {
      showToast('Sua conta esta bloqueada por inadimplencia. Devolva o livro pendente para desbloquear.', 'error');
      setEnviandoEmprestimo(false);
      return;
    }
    const { data: emprestimo, error } = await supabase.from('emprestimos').insert({
      livro_id: modalEmprestimo.livros.id,
      leitor_nome: nomeUsuario,
      leitor_email: emailUsuario,
      leitor_user_id: userId,
      mensagem: mensagemEmprestimo || null,
      prazo_dias: prazoEmprestimo,
      status: 'pendente',
    }).select().single();
    if (!error && emprestimo) {
      await supabase.from('notificacoes').insert({
        user_id: modalEmprestimo.user_id,
        emprestimo_id: emprestimo.id,
        tipo: 'nova_solicitacao',
        lida: false,
      });
      setSucessoEmprestimo(true);
    } else {
      showToast('Erro ao solicitar empréstimo.', 'error');
    }
    setEnviandoEmprestimo(false);
  }

  async function notificarNovosLivros(quantidade) {
    if (!clubeAtivo?.id || !userId) return;
    const { data: outrosMembros } = await supabase
      .from('clube_membros').select('user_id')
      .eq('clube_id', clubeAtivo.id).eq('status', 'aprovado').neq('user_id', userId);
    if (!outrosMembros?.length) return;
    const { data: { user } } = await supabase.auth.getUser();
    const nomeAdicionador = user?.user_metadata?.nome || user?.email?.split('@')[0] || 'Membro';
    await supabase.from('notificacoes').insert(
      outrosMembros.map(m => ({
        user_id: m.user_id, tipo: 'novo_livro_clube', lida: false,
        meta: { nome_clube: clubeAtivo.nome, clube_id: clubeAtivo.id, nome_adicionador: nomeAdicionador, quantidade },
      }))
    );
  }

  async function handleOpcaoTodos() {
    setSalvandoContribuicao(true);
    const { data: livros } = await supabase.from('livros').select('id').eq('user_id', userId);
    if (livros?.length > 0) {
      const inserts = livros.map(l => ({ clube_id: clubeAtivo.id, livro_id: l.id, user_id: userId }));
      await supabase.from('clube_livros').upsert(inserts, { onConflict: 'clube_id,livro_id' });
    }
    await supabase.from('clube_membros').update({ livros_opcao: 'todos' }).eq('id', clubeAtivo.membroEntradaId);
    await notificarNovosLivros(livros?.length || 0);
    await recarregarDetalhe(clubeAtivo.id);
    await buscarClubes();
    setSalvandoContribuicao(false);
    setModalContribuicao(false);
    showToast('Biblioteca completa adicionada ao clube!', 'success');
  }

  async function handleOpcaoSelecionados() {
    await carregarLivrosMembro();
    setEtapaContribuicao('selecionados');
  }

  function toggleLivro(id) {
    setLivrosSelecionados(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handleConfirmarSelecionados() {
    if (livrosSelecionados.size === 0) return;
    setSalvandoContribuicao(true);
    const inserts = [...livrosSelecionados].map(id => ({ clube_id: clubeAtivo.id, livro_id: id, user_id: userId }));
    await supabase.from('clube_livros').upsert(inserts, { onConflict: 'clube_id,livro_id' });
    await supabase.from('clube_membros').update({ livros_opcao: 'selecionados' }).eq('id', clubeAtivo.membroEntradaId);
    await notificarNovosLivros(livrosSelecionados.size);
    await recarregarDetalhe(clubeAtivo.id);
    await buscarClubes();
    setSalvandoContribuicao(false);
    setModalContribuicao(false);
    showToast(`${livrosSelecionados.size} livro${livrosSelecionados.size !== 1 ? 's' : ''} adicionado${livrosSelecionados.size !== 1 ? 's' : ''} ao clube!`, 'success');
  }

  async function handleOpcaoNenhum() {
    await supabase.from('clube_membros').update({ livros_opcao: 'nenhum' }).eq('id', clubeAtivo.membroEntradaId);
    await buscarClubes();
    setModalContribuicao(false);
  }

  async function handleEnviarAviso() {
    if (!textoAviso.trim() || !clubeAtivo?.id) return;
    setEnviandoAviso(true);
    const aprovadosIds = membros
      .filter(m => m.status === 'aprovado' && m.user_id !== userId)
      .map(m => m.user_id);
    if (aprovadosIds.length > 0) {
      await supabase.from('notificacoes').insert(
        aprovadosIds.map(uid => ({
          user_id: uid, tipo: 'mensagem_clube', lida: false,
          meta: { clube_nome: clubeAtivo.nome, clube_id: clubeAtivo.id, mensagem: textoAviso.trim(), remetente: nomeUsuario },
        }))
      );
    }
    setTextoAviso('');
    setModalAviso(false);
    setEnviandoAviso(false);
    showToast('Aviso enviado aos membros!', 'success');
  }

  function copiarLink(slug) {
    navigator.clipboard.writeText(`${window.location.origin}/clube/${slug}`)
      .then(() => showToast('Link copiado!', 'success'))
      .catch(() => showToast('Erro ao copiar link.', 'error'));
  }

  async function abrirModalConvidar() {
    setModalConvidar(true);
    setCarregandoAmigos(true);
    setSelecionadosConvidar(new Set());
    try {
      const { data: segRows } = await supabase.from('seguidores').select('seguido_id').eq('seguidor_id', userId);
      const ids = (segRows || []).map(r => r.seguido_id);
      if (!ids.length) { setAmigosConvidar([]); setCarregandoAmigos(false); return; }
      const { data: perfisData } = await supabase.from('perfis').select('id, nome, username, foto_url').in('id', ids);
      const membrosIds = new Set(membros.map(m => m.user_id));
      setAmigosConvidar((perfisData || []).filter(p => !membrosIds.has(p.id)));
    } catch {
      setAmigosConvidar([]);
    } finally {
      setCarregandoAmigos(false);
    }
  }

  async function enviarConvites() {
    if (!selecionadosConvidar.size) return;
    setEnviandoConvite(true);
    try {
      const notifs = [...selecionadosConvidar].map(uid => ({
        user_id: uid,
        tipo: 'convite_clube',
        lida: false,
        meta: {
          clube_id: clubeAtivo.id,
          clube_nome: clubeAtivo.nome,
          clube_slug: clubeAtivo.slug,
          clube_publico: clubeAtivo.publico !== false,
          convidado_por_id: userId,
          convidado_por_nome: nomeUsuario,
        },
      }));
      await supabase.from('notificacoes').insert(notifs);
      showToast(`Convite${notifs.length > 1 ? 's' : ''} enviado${notifs.length > 1 ? 's' : ''}!`, 'success');
      setModalConvidar(false);
    } catch {
      showToast('Erro ao enviar convites. Tente novamente.', 'error');
    } finally {
      setEnviandoConvite(false);
    }
  }

  // Precisa estar antes dos early returns para uso no painel desktop de configurações
  const busca = buscaAtiva.toLowerCase().trim();
  const entradasFiltradas = sortClubes(
    entradas.filter(e => !busca || e.clubes?.nome?.toLowerCase().includes(busca) || e.clubes?.descricao?.toLowerCase().includes(busca)),
    ordenacao
  );

  // ── CRIAR ──────────────────────────────────────────────
  if (tela === 'criar') {
    return (
      <div className="bg-paper" style={{height:'100dvh', overflowY:'auto'}}>
        <div className="max-w-md mx-auto px-4 pb-6 pt-safe-6">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => setTela('lista')} className="text-sm text-ink-400 hover:text-ink-500">← Voltar</button>
            <h1 className="text-xl font-medium text-ink-900">Criar clube</h1>
          </div>
          <div className="bg-white border border-line-soft rounded-2xl p-5 flex flex-col gap-4">
            <div>
              <label className="text-xs text-ink-400 mb-1 block">Nome do clube *</label>
              <input type="text" value={nome} onChange={e => setNome(e.target.value)}
                placeholder="Ex: Clube do livro da turma"
                className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line" />
            </div>
            <div>
              <label className="text-xs text-ink-400 mb-1 block">Descrição <span className="text-ink-300">(opcional)</span></label>
              <textarea value={descricao} onChange={e => setDescricao(e.target.value)}
                placeholder="Sobre o clube..." rows={3}
                className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line resize-none" />
            </div>
            <div>
              <label className="text-xs text-ink-400 mb-2 block">Cor do clube</label>
              <div className="flex gap-2">
                {CLUB_GRADIENTS.map((g, i) => (
                  <button key={i} type="button" onClick={() => setCorIndex(i)}
                    className="w-8 h-8 rounded-full flex-shrink-0 border-2 transition-all"
                    style={{background: `linear-gradient(135deg, ${g[0]}, ${g[1]})`, borderColor: corIndex === i ? '#5B3CE4' : 'transparent'}} />
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-ink-900">Clube privado</p>
                <p className="text-xs text-ink-400">Membros entram por convite</p>
              </div>
              <button type="button" onClick={() => setPrivado(v => !v)}
                className="relative w-11 h-6 rounded-full transition-colors"
                style={{background: privado ? '#5B3CE4' : '#ECE7E1'}}>
                <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
                  style={{transform: privado ? 'translateX(20px)' : 'translateX(0)'}} />
              </button>
            </div>
            {erroForm && <p className="text-xs text-red-500">{erroForm}</p>}
            <button onClick={handleCriarClube} disabled={salvando}
              className="w-full bg-brand text-white py-3 rounded-xl text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-50">
              {salvando ? 'Criando...' : 'Criar clube'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── CONFIGURAÇÕES ────────────────────────────────────────
  if (tela === 'configuracoes' && clubeAtivo) {
    const bloqueados = membros.filter(m => m.status === 'bloqueado');
    const capaAtual = capaPreview || clubeAtivo.capa_url;

    const configFormContent = (
      <>
        {/* Capa */}
        <div className="bg-white border border-line-soft rounded-2xl overflow-hidden mb-4">
          <div className="h-36 bg-paper flex items-center justify-center relative overflow-hidden">
            {capaAtual
              ? <img src={capaAtual} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full" style={{background:`linear-gradient(135deg, ${CLUB_GRADIENTS[clubeAtivo?.cor_index ?? 0]?.[0] ?? '#5B3CE4'}, ${CLUB_GRADIENTS[clubeAtivo?.cor_index ?? 0]?.[1] ?? '#7C5DEF'})`}} />}
            <button
              type="button"
              onClick={() => capaInputRef.current?.click()}
              className="absolute bottom-2 right-2 bg-black/50 text-white text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
              {capaAtual ? 'Trocar' : 'Adicionar capa'}
            </button>
            <input ref={capaInputRef} type="file" accept="image/*" className="hidden"
              onChange={e => { const f = e.target.files[0]; if (f) { setEditCapa(f); setCapaPreview(URL.createObjectURL(f)); } }} />
          </div>
        </div>

        {/* Foto do clube (avatar) */}
        <div className="bg-white border border-line-soft rounded-2xl p-5 mb-4">
          <p className="text-xs font-semibold text-ink-400 uppercase tracking-wider mb-3">Foto do clube</p>
          <input ref={fotoInputRef} type="file" accept="image/*" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) { setEditFoto(f); setFotoPreview(URL.createObjectURL(f)); } }} />
          <div className="flex items-center gap-4">
            <div onClick={() => fotoInputRef.current?.click()}
              className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0 cursor-pointer"
              style={{background:`linear-gradient(135deg,${CLUB_GRADIENTS[clubeAtivo?.cor_index ?? 0]?.[0] ?? '#5B3CE4'},${CLUB_GRADIENTS[clubeAtivo?.cor_index ?? 0]?.[1] ?? '#7C5DEF'})`}}>
              {(fotoPreview || clubeAtivo.foto_url)
                ? <img src={fotoPreview || clubeAtivo.foto_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-2xl font-extrabold text-white">
                    {(clubeAtivo.nome || 'C').charAt(0).toUpperCase()}
                  </div>}
            </div>
            <div>
              <button type="button" onClick={() => fotoInputRef.current?.click()}
                className="text-sm font-semibold text-brand border-0 bg-transparent cursor-pointer p-0">
                {(fotoPreview || clubeAtivo.foto_url) ? 'Trocar foto' : 'Adicionar foto'}
              </button>
              {(fotoPreview || clubeAtivo.foto_url) && (
                <button type="button" onClick={() => { setEditFoto(null); setFotoPreview(null); }}
                  className="block text-xs text-ink-400 bg-transparent border-0 cursor-pointer p-0 mt-1">
                  Remover
                </button>
              )}
              <p className="text-xs text-ink-300 mt-1">Aparece no avatar e nos cards do clube</p>
            </div>
          </div>
        </div>

        {/* Formulário */}
        <div className="bg-white border border-line-soft rounded-2xl p-5 mb-4 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ink-900">{editPublico ? 'Clube público' : 'Clube privado'}</p>
              <p className="text-xs text-ink-400 mt-0.5">
                {editPublico ? 'Qualquer pessoa com o link pode solicitar entrada' : 'Apenas membros convidados podem entrar'}
              </p>
            </div>
            <Toggle value={editPublico} onChange={setEditPublico} />
          </div>
          <div className="border-t border-gray-50" />
          <div>
            <label className="text-xs text-ink-400 mb-1 block">Nome do clube</label>
            <input value={editNome} onChange={e => setEditNome(e.target.value)}
              className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line" />
          </div>
          <div>
            <label className="text-xs text-ink-400 mb-1 block">Descrição</label>
            <textarea value={editDescricao} onChange={e => setEditDescricao(e.target.value)}
              rows={3} placeholder="Sobre o clube..."
              className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line resize-none" />
          </div>
          <button onClick={handleSalvarConfig} disabled={salvandoConfig || !editNome.trim()}
            className="w-full bg-brand text-white py-3 rounded-xl text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-50">
            {salvandoConfig ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </div>

        {/* Membros bloqueados */}
        {bloqueados.length > 0 && (
          <div className="bg-white border border-line-soft rounded-2xl p-5 mb-4">
            <p className="text-sm font-medium text-ink-900 mb-3">Bloqueados ({bloqueados.length})</p>
            <div className="flex flex-col gap-3">
              {bloqueados.map(m => (
                <div key={m.id} className="flex items-center gap-3">
                  <Avatar nome={m.perfil?.nome} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-ink-900">{m.perfil?.nome || 'Usuário'}</p>
                    {m.perfil?.username && <p className="text-xs text-ink-400">@{m.perfil.username}</p>}
                  </div>
                  <button onClick={() => handleDesbloquear(m.id)}
                    className="text-xs text-brand hover:text-brand-dark transition-colors flex-shrink-0">
                    Desbloquear
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Zona de perigo */}
        <div className="bg-white border border-line-soft rounded-2xl p-5 flex flex-col gap-3">
          <button
            onClick={userId === clubeAtivo?.criador_id ? () => setModalSairFounder(true) : handleSairDoClube}
            className="w-full py-2.5 text-sm text-ink-500 border border-line rounded-xl hover:bg-paper transition-colors">
            Sair do clube
          </button>
          {!confirmandoDelete ? (
            <button onClick={() => setConfirmandoDelete(true)}
              className="w-full py-2.5 text-sm text-red-400 border border-red-100 rounded-xl hover:bg-red-50 transition-colors">
              Excluir clube
            </button>
          ) : (
            <div className="border border-red-100 rounded-xl p-4">
              <p className="text-sm font-medium text-ink-900 mb-1">Excluir clube?</p>
              <p className="text-xs text-ink-400 mb-4">Todos os membros e livros serão removidos. Essa ação não pode ser desfeita.</p>
              <div className="flex gap-2">
                <button onClick={handleDeletarClube}
                  className="flex-1 py-2 text-xs bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">
                  Sim, excluir
                </button>
                <button onClick={() => setConfirmandoDelete(false)}
                  className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );

    return (
      <>
      {/* ── Mobile ── */}
      <div className="lg:hidden bg-paper" style={{height:'100dvh', overflowY:'auto'}}>
        <div className="max-w-md mx-auto px-4 pb-10 pt-safe-6">
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => setTela('detalhe')} className="text-sm text-ink-400 hover:text-ink-500">← Voltar</button>
            <h1 className="text-base font-medium text-ink-900">Configurações do clube</h1>
          </div>
          {configFormContent}
        </div>
      </div>

      {/* ── Desktop ── */}
      <div className="hidden lg:flex" style={{height:'100dvh', overflow:'hidden'}}>
        {/* Left panel: club list */}
        <div style={{width:280,flexShrink:0,borderRight:'1px solid #EDE9F5',display:'flex',flexDirection:'column',height:'100%'}}>
          <div style={{padding:'28px 16px 12px'}}>
            <p style={{fontSize:10,fontWeight:800,letterSpacing:'.15em',textTransform:'uppercase',color:'#5B3CE4',margin:'0 0 4px'}}>CLUBES</p>
            <h1 style={{fontSize:22,fontWeight:800,color:'#211C2B',margin:'0 0 14px',lineHeight:1.1}}>Seus clubes de leitura</h1>
          </div>
          <div style={{flex:1,overflowY:'auto',scrollbarWidth:'none'}}>
            {entradasFiltradas.map(entrada => {
              const clube = entrada.clubes;
              if (!clube) return null;
              const [g1, g2] = getClubGradient(clube.id);
              const isSelected = clubeAtivo?.id === clube.id;
              const memCount = (membrosMap[clube.id] || []).length;
              return (
                <div key={clube.id} onClick={() => selecionarClubeDesktop(entrada)}
                  style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',cursor:'pointer',background:isSelected?'#EDE9F5':'transparent',transition:'background 0.15s'}}>
                  <div style={{width:42,height:42,borderRadius:12,background:`linear-gradient(135deg,${g1},${g2})`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:17,fontWeight:800,color:'#fff',overflow:'hidden'}}>
                    {clube.foto_url ? <img src={clube.foto_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} /> : (clube.nome||'?').charAt(0).toUpperCase()}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontWeight:700,fontSize:14,color:'#211C2B',margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{clube.nome}</p>
                    <p style={{fontSize:12,color:'#9A93A4',margin:0}}>{memCount} {memCount===1?'membro':'membros'}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{padding:'12px 16px',borderTop:'1px solid #EDE9F5'}}>
            <button onClick={() => { setNome(''); setDescricao(''); setPrivado(false); setCorIndex(0); setModalCriarAberto(true); }}
              style={{width:'100%',padding:'10px',background:'#5B3CE4',border:'none',borderRadius:12,fontSize:13,fontWeight:700,color:'#fff',cursor:'pointer'}}>
              + Criar clube
            </button>
          </div>
        </div>

        {/* Right panel: config form */}
        <div style={{flex:1,overflowY:'auto',scrollbarWidth:'none',background:'#F6F5FB'}}>
          <div style={{maxWidth:640,margin:'0 auto',padding:'40px 40px 80px'}}>
            <button onClick={() => setTela('detalhe')}
              style={{display:'inline-flex',alignItems:'center',gap:6,background:'none',border:'none',cursor:'pointer',color:'#9A93A4',fontSize:13,fontWeight:600,padding:0,marginBottom:24}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              Voltar para o clube
            </button>
            <h1 style={{fontSize:24,fontWeight:800,color:'#211C2B',margin:'0 0 24px',lineHeight:1.1}}>Configurações do clube</h1>
            {configFormContent}
          </div>
        </div>
      </div>

      {/* Modal: Founder saindo do clube */}
      {modalSairFounder && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{background:'rgba(0,0,0,0.4)'}}>
          <div className="bg-white w-full max-w-md rounded-t-2xl p-6">
            <p className="text-base font-bold text-ink-900 mb-1">Você é o Founder</p>
            <p className="text-sm text-ink-400 mb-5">
              Ao sair, o admin mais antigo assumirá como novo Founder. Se não houver outro admin, o membro mais antigo será promovido.
            </p>
            <div className="flex flex-col gap-2">
              <button onClick={handleSairFounder}
                className="w-full py-3 text-sm font-medium text-ink-700 border border-line rounded-xl hover:bg-paper transition-colors">
                Sair do clube
              </button>
              <button onClick={() => { setModalSairFounder(false); setConfirmandoDelete(true); }}
                className="w-full py-3 text-sm font-medium text-red-500 border border-red-100 rounded-xl hover:bg-red-50 transition-colors">
                Sair e excluir o clube
              </button>
              <button onClick={() => setModalSairFounder(false)}
                className="w-full py-3 text-sm text-ink-400 hover:text-ink-500 transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: aviso ao clube */}
      {modalAviso && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{background:'rgba(0,0,0,0.4)'}}>
          <div className="bg-white w-full max-w-md rounded-t-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-bold text-ink-900">Enviar aviso ao clube</p>
              <button onClick={() => setModalAviso(false)}>
                <X size={18} strokeWidth={2} color="#9A93A4" />
              </button>
            </div>
            <p className="text-xs text-ink-400 mb-3">Todos os membros receberão uma notificação com este aviso.</p>
            <textarea
              placeholder="Ex: Discussão do cap. 10 no sábado às 19h!"
              value={textoAviso}
              onChange={e => setTextoAviso(e.target.value)}
              rows={4}
              className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm outline-none focus:border-line resize-none mb-4 text-ink-900"
            />
            <div className="flex gap-2">
              <button onClick={handleEnviarAviso} disabled={!textoAviso.trim() || enviandoAviso}
                className="flex-1 py-3 text-sm font-bold text-white rounded-xl border-0 cursor-pointer disabled:opacity-50"
                style={{background:'#5B3CE4'}}>
                {enviandoAviso ? 'Enviando...' : 'Enviar aviso'}
              </button>
              <button onClick={() => setModalAviso(false)}
                className="flex-1 py-3 text-sm border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }

  // ── DETALHE ─────────────────────────────────────────────
  if (tela === 'detalhe' && clubeAtivo) {
    const isFounder = clubeAtivo.criador_id === userId;
    const isAdmin = clubeAtivo.meuRole === 'admin' || isFounder;
    const pendentes = membros.filter(m => m.status === 'pendente');

    return (
      <>
      {(() => { const [g1,g2] = getClubGradient(clubeAtivo.id); return (
    <div style={{height:'100dvh', display:'flex', flexDirection:'column', background:'#F7F5F2'}}>

      {/* Header fixo */}
      <div style={{background:'#fff', paddingTop:'env(safe-area-inset-top)', borderBottom:'1px solid #EDE9F5', flexShrink:0}}>
        <div style={{padding:'12px 16px 0'}}>
          {/* Linha voltar */}
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16}}>
            <button onClick={() => setTela('lista')} style={{background:'none', border:'none', cursor:'pointer', color:'#9A93A4', fontSize:13, fontWeight:600, padding:0, display:'flex', alignItems:'center', gap:4}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              Voltar
            </button>
            <button onClick={() => copiarLink(clubeAtivo.slug)} style={{background:'none', border:'none', cursor:'pointer', color:'#9A93A4', padding:4}}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
            </button>
          </div>

          {/* Info do clube */}
          <div style={{display:'flex', alignItems:'flex-start', gap:14, marginBottom:14}}>
            <div style={{width:56, height:56, borderRadius:14, background:`linear-gradient(135deg,${g1},${g2})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:800, color:'#fff', flexShrink:0, overflow:'hidden'}}>
              {clubeAtivo.foto_url
                ? <img src={clubeAtivo.foto_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                : (clubeAtivo.nome||'?').charAt(0).toUpperCase()}
            </div>
            <div style={{flex:1, minWidth:0}}>
              <h2 style={{fontSize:18, fontWeight:800, color:'#211C2B', margin:'0 0 3px', lineHeight:1.1}}>{clubeAtivo.nome}</h2>
              <p style={{fontSize:12, color:'#9A93A4', margin:'0 0 8px'}}>
                {membros.filter(m=>m.status==='aprovado').length} membros · {clubeAtivo.publico!==false?'público':'privado'}
              </p>
              <MemberBubbles membros={membros.filter(m=>m.status==='aprovado').slice(0,6).map(m=>m.perfil||{nome:null,foto_url:null})} />
            </div>
          </div>

          {/* Botões de ação */}
          <div style={{display:'flex', gap:8, marginBottom:14}}>
            {isFounder && (
              <button onClick={abrirConfiguracoes}
                style={{width:36, height:36, borderRadius:999, background:'#F2EEFE', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#5B3CE4', flexShrink:0}}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              </button>
            )}
            <button onClick={isFounder ? () => setModalSairFounder(true) : handleSairDoClube}
              style={{padding:'8px 14px', borderRadius:999, background:'#FFF5F3', color:'#E55B3C', fontWeight:700, fontSize:12, border:'none', cursor:'pointer'}}>
              Sair do clube
            </button>
            <button onClick={abrirModalConvidar}
              style={{padding:'8px 16px', borderRadius:999, background:'#5B3CE4', color:'#fff', fontWeight:700, fontSize:12, border:'none', cursor:'pointer'}}>
              Convidar
            </button>
          </div>

          {/* Tabs */}
          <div style={{display:'flex', gap:2, borderBottom:'2px solid #EDE9F5'}}>
            {[['estante','Estante'],['membros','Membros'],['atividade','Atividade']].map(([k,l]) => (
              <button key={k} onClick={() => setDesktopAba(k)}
                style={{padding:'8px 16px', border:'none', background:'transparent', cursor:'pointer', fontWeight:700, fontSize:13,
                  color: desktopAba===k ? '#5B3CE4' : '#9A93A4',
                  borderBottom: desktopAba===k ? '2px solid #5B3CE4' : '2px solid transparent',
                  marginBottom: -2}}>
                {l}{k==='membros' && isAdmin && pendentes.length > 0 ? ` (${pendentes.length})` : ''}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Conteúdo scrollável */}
      <div style={{flex:1, overflowY:'auto', scrollbarWidth:'none'}}>
        <div style={{padding:'16px 16px 120px'}}>

          {/* Modal: contribuição de livros */}
          {modalContribuicao && (
            <div className="fixed inset-0 flex items-end justify-center z-50 px-4 pb-6" style={{background:'rgba(0,0,0,0.3)'}}>
              <div className="bg-white rounded-2xl p-5 w-full max-w-sm">
                {etapaContribuicao === 'opcao' ? (
                  <>
                    <p className="text-sm font-medium text-ink-900 mb-1">Bem-vindo ao clube!</p>
                    <p className="text-xs text-ink-400 mb-5">Como deseja contribuir com o acervo do clube?</p>
                    <div className="flex flex-col gap-2">
                      <button onClick={handleOpcaoTodos} disabled={salvandoContribuicao}
                        className="w-full text-left px-4 py-3.5 border border-line-soft rounded-xl hover:border-line transition-colors disabled:opacity-50">
                        <p className="text-sm font-bold text-ink-900">Adicionar todos</p>
                        <p className="text-xs text-ink-400 mt-0.5">Todos os seus livros ficam visíveis para o grupo</p>
                      </button>
                      <button onClick={handleOpcaoSelecionados} disabled={salvandoContribuicao}
                        className="w-full text-left px-4 py-3.5 border border-line-soft rounded-xl hover:border-line transition-colors disabled:opacity-50">
                        <p className="text-sm font-bold text-ink-900">Escolher livros</p>
                        <p className="text-xs text-ink-400 mt-0.5">Selecione quais livros compartilhar</p>
                      </button>
                      <button onClick={handleOpcaoNenhum}
                        className="w-full py-3 text-xs text-ink-400 hover:text-ink-500 transition-colors">
                        Fechar
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-ink-900 mb-1">Escolha os livros</p>
                    <p className="text-xs text-ink-400 mb-4">Selecione quais livros compartilhar com o clube</p>
                    <div className="max-h-64 overflow-y-auto flex flex-col gap-1 mb-4">
                      {livrosMembro.length === 0
                        ? <p className="text-xs text-ink-400 text-center py-4">Nenhum livro no seu acervo ainda.</p>
                        : livrosMembro.map(livro => (
                          <label key={livro.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-paper cursor-pointer">
                            <input type="checkbox" checked={livrosSelecionados.has(livro.id)}
                              onChange={() => toggleLivro(livro.id)}
                              className="w-4 h-4 accent-gray-900 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-ink-900 truncate">{livro.titulo}</p>
                              <p className="text-xs text-ink-400 truncate">{livro.autor}</p>
                            </div>
                          </label>
                        ))}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleConfirmarSelecionados}
                        disabled={livrosSelecionados.size === 0 || salvandoContribuicao}
                        className="flex-1 py-2.5 text-xs bg-brand text-white rounded-xl hover:bg-brand-dark transition-colors disabled:opacity-40">
                        {salvandoContribuicao ? 'Salvando...' : `Confirmar (${livrosSelecionados.size})`}
                      </button>
                      <button onClick={() => setEtapaContribuicao('opcao')}
                        className="flex-1 py-2.5 text-xs border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">
                        Voltar
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {membroMenuId && <div style={{position:'fixed',inset:0,zIndex:10}} onClick={() => setMembroMenuId(null)} />}

          {carregandoDetalhe ? (
            <p style={{textAlign:'center', color:'#9A93A4', fontSize:14, paddingTop:48}}>Carregando...</p>
          ) : (
            <>
              {/* ABA: ESTANTE */}
              {desktopAba === 'estante' && (() => {
                const bgs = ['linear-gradient(160deg,#2D3B55,#1A2D45)','linear-gradient(160deg,#3D6B2E,#1E4A1A)','linear-gradient(160deg,#6E3030,#3D1A1A)','linear-gradient(160deg,#4A3B6E,#2D1A55)'];
                return (
                  <>
                    {livrosClube.length === 0 ? (
                      <div style={{textAlign:'center', padding:'40px 24px'}}>
                        <div style={{width:56, height:56, borderRadius:16, background:'#EDE9FF', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px'}}>
                          <BookOpen size={24} color="#5B3CE4" strokeWidth={1.5} />
                        </div>
                        <p style={{fontSize:15, fontWeight:700, color:'#211C2B', margin:'0 0 6px'}}>A estante está vazia</p>
                        <p style={{fontSize:13, color:'#9A93A4', margin:'0 0 20px', lineHeight:1.5}}>
                          {clubeAtivo.meuStatus === 'aprovado'
                            ? 'Adicione um livro do seu acervo para compartilhar com o grupo.'
                            : 'Nenhum membro adicionou livros ainda.'}
                        </p>
                        {clubeAtivo.meuStatus === 'aprovado' && (
                          <button onClick={() => { setModalContribuicao(true); setEtapaContribuicao('opcao'); setLivrosSelecionados(new Set()); }}
                            style={{padding:'11px 24px', borderRadius:12, background:'#5B3CE4', color:'#fff', fontWeight:700, fontSize:13, border:'none', cursor:'pointer'}}>
                            Adicionar livro ao clube
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16}}>
                          {livrosClube.map(cl => {
                            const livro = cl.livros;
                            if (!livro) return null;
                            return (
                              <div key={cl.id} style={{borderRadius:12, overflow:'hidden', position:'relative', background: livro.capa_url ? '#ccc' : bgs[livro.titulo?.charCodeAt(0) % bgs.length || 0], aspectRatio:'2/3'}}>
                                {livro.capa_url
                                  ? <img src={livro.capa_url} alt={livro.titulo} style={{width:'100%', height:'100%', objectFit:'cover'}} />
                                  : <div style={{position:'absolute', inset:0, display:'flex', flexDirection:'column', justifyContent:'flex-end', padding:'8px 10px', background:'linear-gradient(0deg,rgba(0,0,0,0.65),transparent)'}}>
                                      <p style={{color:'#fff', fontWeight:700, fontSize:12, margin:0}}>{livro.titulo}</p>
                                      <p style={{color:'rgba(255,255,255,0.65)', fontSize:10, margin:0}}>{livro.autor}</p>
                                    </div>}
                                {livro.disponivel && cl.user_id !== userId && clubeAtivo.meuStatus === 'aprovado' && (
                                  <button onClick={() => { setModalEmprestimo(cl); setSucessoEmprestimo(false); setMensagemEmprestimo(''); setPrazoEmprestimo(14); }}
                                    style={{position:'absolute', bottom:8, left:8, right:8, padding:'6px', borderRadius:8, background:'rgba(91,60,228,0.92)', color:'#fff', fontWeight:700, fontSize:11, border:'none', cursor:'pointer'}}>
                                    Pedir emprestado
                                  </button>
                                )}
                                {cl.user_id === userId && (
                                  <button onClick={() => handleRemoverLivro(cl.id)}
                                    style={{position:'absolute', top:6, right:6, width:24, height:24, borderRadius:'50%', background:'rgba(0,0,0,0.45)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff'}}>
                                    <X size={12} strokeWidth={2.5} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {clubeAtivo.meuStatus === 'aprovado' && (
                          <div style={{display:'flex', gap:10}}>
                            <button onClick={() => { setModalContribuicao(true); setEtapaContribuicao('opcao'); setLivrosSelecionados(new Set()); }}
                              style={{padding:'10px 18px', borderRadius:12, background:'#5B3CE4', color:'#fff', fontWeight:700, fontSize:13, border:'none', cursor:'pointer'}}>
                              Adicionar livro
                            </button>
                            {isAdmin && (
                              <button onClick={() => { setTextoAviso(''); setModalAviso(true); }}
                                style={{padding:'10px 18px', borderRadius:12, background:'#F0ECFE', color:'#5B3CE4', fontWeight:700, fontSize:13, border:'none', cursor:'pointer'}}>
                                Enviar aviso
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {livrosClube.length === 0 && isAdmin && clubeAtivo.meuStatus === 'aprovado' && (
                      <div style={{textAlign:'center', marginTop:12}}>
                        <button onClick={() => { setTextoAviso(''); setModalAviso(true); }}
                          style={{padding:'10px 18px', borderRadius:12, background:'#F0ECFE', color:'#5B3CE4', fontWeight:700, fontSize:13, border:'none', cursor:'pointer'}}>
                          Enviar aviso ao clube
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ABA: MEMBROS */}
              {desktopAba === 'membros' && (() => {
                const dIsFounder = clubeAtivo.criador_id === userId;
                const dIsAdmin = clubeAtivo.meuRole === 'admin' || dIsFounder;
                const dPendentes = membros.filter(m => m.status === 'pendente');
                const dAprovados = membros.filter(m => m.status === 'aprovado');
                return (
                  <div style={{display:'flex', flexDirection:'column', gap:16}}>
                    {dIsAdmin && dPendentes.length > 0 && (
                      <div>
                        <p style={{fontSize:13, fontWeight:700, color:'#211C2B', margin:'0 0 10px'}}>
                          Pedidos de entrada
                          <span style={{marginLeft:8, fontSize:11, fontWeight:800, background:'#211C2B', color:'#fff', padding:'2px 7px', borderRadius:999}}>{dPendentes.length}</span>
                        </p>
                        {dPendentes.map(m => (
                          <div key={m.id} style={{display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:'#fff', borderRadius:12, boxShadow:'0 1px 3px rgba(33,28,43,0.06)', marginBottom:6}}>
                            <div style={{width:36, height:36, borderRadius:'50%', background:'#EDE9FF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:'#5B3CE4', flexShrink:0}}>{(m.perfil?.nome||'?').charAt(0).toUpperCase()}</div>
                            <div style={{flex:1, minWidth:0}}>
                              <p style={{fontWeight:600, fontSize:13, color:'#211C2B', margin:0}}>{m.perfil?.nome||'Usuário'}</p>
                              {m.perfil?.username && <p style={{fontSize:11, color:'#9A93A4', margin:'1px 0 0'}}>@{m.perfil.username}</p>}
                            </div>
                            <div style={{display:'flex', gap:8}}>
                              <button onClick={() => handleAprovar(m.id)} style={{padding:'6px 14px', background:'#5B3CE4', color:'#fff', fontSize:12, fontWeight:700, border:'none', borderRadius:8, cursor:'pointer'}}>Aprovar</button>
                              <button onClick={() => handleRejeitar(m.id)} style={{padding:'6px 14px', background:'transparent', color:'#9A93A4', fontSize:12, fontWeight:600, border:'1px solid #EDE9F5', borderRadius:8, cursor:'pointer'}}>Recusar</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div>
                      <p style={{fontSize:13, fontWeight:700, color:'#211C2B', margin:'0 0 10px'}}>Membros ({dAprovados.length})</p>
                      <div style={{display:'flex', flexDirection:'column', gap:6}}>
                        {dAprovados.map(m => {
                          const isMemberFounder = m.user_id === clubeAtivo.criador_id;
                          const isMe = m.user_id === userId;
                          const showAdminMenu = dIsAdmin && !isMemberFounder && !isMe;
                          const showSelfMenu = dIsAdmin && !dIsFounder && isMe;
                          return (
                            <div key={m.id} style={{display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:'#fff', borderRadius:12, boxShadow:'0 1px 3px rgba(33,28,43,0.06)', position:'relative', zIndex:1}}>
                              <div style={{width:36, height:36, borderRadius:'50%', background:getAvatarColor(m.perfil?.nome), display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:'#fff', flexShrink:0, overflow:'hidden'}}>
                                {m.perfil?.foto_url ? <img src={m.perfil.foto_url} style={{width:'100%', height:'100%', objectFit:'cover'}} alt="" /> : (m.perfil?.nome||'?').charAt(0).toUpperCase()}
                              </div>
                              <div style={{flex:1, minWidth:0}}>
                                <p style={{fontWeight:600, fontSize:13, color:'#211C2B', margin:0}}>{m.perfil?.nome||'Usuário'}</p>
                                {m.perfil?.username && <p style={{fontSize:11, color:'#9A93A4', margin:'1px 0 0'}}>@{m.perfil.username}</p>}
                              </div>
                              <span style={{fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:999, display:'flex', alignItems:'center', gap:4, flexShrink:0,
                                background: isMemberFounder ? '#5B3CE4' : m.role==='admin' ? '#211C2B' : '#F2EEFE',
                                color: isMemberFounder ? '#fff' : m.role==='admin' ? '#fff' : '#5B3CE4'}}>
                                {isMemberFounder && <Crown size={9} strokeWidth={2.5} />}
                                {isMemberFounder ? 'Founder' : m.role==='admin' ? 'Admin' : 'Membro'}
                              </span>
                              {(showAdminMenu || showSelfMenu) && (
                                <div style={{position:'relative', flexShrink:0, zIndex:20}}>
                                  <button onClick={e => { e.stopPropagation(); setMembroMenuId(membroMenuId===m.id ? null : m.id); }}
                                    style={{width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', background:'transparent', border:'none', cursor:'pointer', color:'#C4BECC', borderRadius:'50%'}}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                                  </button>
                                  {membroMenuId === m.id && (
                                    <div style={{position:'absolute', right:0, top:32, background:'#fff', border:'1px solid #EDE9F5', borderRadius:12, boxShadow:'0 4px 16px rgba(0,0,0,.12)', zIndex:30, minWidth:170, overflow:'hidden'}}>
                                      {showAdminMenu && m.role!=='admin' && <button onClick={() => handlePromoverAdmin(m.id)} style={{width:'100%', textAlign:'left', padding:'10px 14px', fontSize:12, color:'#211C2B', border:'none', background:'transparent', cursor:'pointer', display:'block'}}>Promover a admin</button>}
                                      {showAdminMenu && m.role==='admin' && <button onClick={() => handleDemoverAdmin(m.id)} style={{width:'100%', textAlign:'left', padding:'10px 14px', fontSize:12, color:'#211C2B', border:'none', background:'transparent', cursor:'pointer', display:'block'}}>Rebaixar para membro</button>}
                                      {showAdminMenu && <button onClick={() => handleRemoverMembro(m.id)} style={{width:'100%', textAlign:'left', padding:'10px 14px', fontSize:12, color:'#E55B3C', border:'none', background:'transparent', cursor:'pointer', display:'block'}}>Remover do clube</button>}
                                      {showAdminMenu && <button onClick={() => handleBloquearMembro(m.id)} style={{width:'100%', textAlign:'left', padding:'10px 14px', fontSize:12, color:'#E55B3C', border:'none', background:'transparent', cursor:'pointer', display:'block'}}>Bloquear</button>}
                                      {showSelfMenu && <button onClick={() => handleDemoverAdmin(m.id)} style={{width:'100%', textAlign:'left', padding:'10px 14px', fontSize:12, color:'#211C2B', border:'none', background:'transparent', cursor:'pointer', display:'block'}}>Deixar de ser admin</button>}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ABA: ATIVIDADE */}
              {desktopAba === 'atividade' && (
                <>
                  <p style={{fontSize:15, fontWeight:800, color:'#211C2B', margin:'0 0 14px'}}>Atividade recente</p>
                  {feedAtividade.length === 0 ? (
                    <div style={{textAlign:'center', paddingTop:40}}>
                      <p style={{fontSize:14, fontWeight:700, color:'#211C2B', margin:'0 0 6px'}}>Nenhuma atividade ainda</p>
                      <p style={{fontSize:13, color:'#9A93A4'}}>As ações do clube aparecerão aqui.</p>
                    </div>
                  ) : (
                    <div style={{display:'flex', flexDirection:'column', gap:10}}>
                      {feedAtividade.map((evt, i) => (
                        <div key={i} style={{display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:'#fff', borderRadius:12, boxShadow:'0 1px 3px rgba(33,28,43,0.06)'}}>
                          <div style={{width:36, height:36, borderRadius:'50%', background:getAvatarColor(evt.nome), display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:'#fff', flexShrink:0}}>
                            {(evt.nome||'?').charAt(0).toUpperCase()}
                          </div>
                          <p style={{fontSize:13, color:'#211C2B', margin:0, flex:1}}>
                            <strong>{evt.nome}</strong>{evt.tipo==='entrou' ? ' entrou no clube' : <> adicionou <em>"{evt.livro}"</em></>}
                          </p>
                          <span style={{fontSize:11, color:'#9A93A4', flexShrink:0}}>{tempoRelativo(evt.ts)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {isAdmin && (
                    <button onClick={() => { setTextoAviso(''); setModalAviso(true); }}
                      style={{marginTop:16, width:'100%', padding:'11px', borderRadius:12, background:'#F0ECFE', color:'#5B3CE4', fontWeight:700, fontSize:13, border:'none', cursor:'pointer'}}>
                      Enviar aviso ao clube
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    ); })()}

      {/* MODAL SOLICITAR EMPRÉSTIMO */}
      {modalEmprestimo && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{background:'rgba(0,0,0,0.4)'}}>
          <div className="bg-white w-full max-w-md rounded-t-2xl p-5 max-h-[90vh] overflow-y-auto">
            {sucessoEmprestimo ? (
              <div className="text-center py-6">
                <p className="text-base font-medium text-ink-900 mb-1">Solicitação enviada!</p>
                <p className="text-sm text-ink-400 mb-5">Aguarde o dono aprovar o empréstimo.</p>
                <button onClick={() => setModalEmprestimo(null)}
                  className="w-full py-3 text-sm border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">
                  Fechar
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-medium text-ink-900">Solicitar empréstimo</p>
                  <button onClick={() => setModalEmprestimo(null)} className="text-ink-400 flex items-center"><X size={18} strokeWidth={2} /></button>
                </div>

                <div className="flex gap-3 pb-4 mb-4 border-b border-gray-50">
                  <div className="w-10 h-14 bg-paper rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {modalEmprestimo.livros?.capa_url
                      ? <img src={modalEmprestimo.livros.capa_url} alt="" className="w-full h-full object-cover" />
                      : <BookOpen size={20} color="#BDB7C5" strokeWidth={1.5} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-900 leading-tight">{modalEmprestimo.livros?.titulo}</p>
                    <p className="text-xs text-ink-400 mt-0.5">{modalEmprestimo.livros?.autor}</p>
                  </div>
                </div>

                <div className="bg-paper rounded-xl px-4 py-3 mb-4">
                  <p className="text-xs text-ink-400">Solicitando como</p>
                  <p className="text-sm font-medium text-ink-900 mt-0.5">{nomeUsuario || emailUsuario}</p>
                </div>

                <div className="mb-4">
                  <label className="text-xs text-ink-400 mb-1 block">Prazo desejado</label>
                  <select value={prazoEmprestimo} onChange={e => setPrazoEmprestimo(parseInt(e.target.value))}
                    className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm bg-paper outline-none focus:border-line">
                    <option value={7}>1 semana</option>
                    <option value={14}>2 semanas</option>
                    <option value={21}>3 semanas</option>
                    <option value={30}>1 mês</option>
                    <option value={0}>Sem prazo definido</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="text-xs text-ink-400 mb-1 block">Mensagem <span className="text-ink-300">(opcional)</span></label>
                  <textarea
                    placeholder="Oi! Posso pegar emprestado?"
                    value={mensagemEmprestimo}
                    onChange={e => setMensagemEmprestimo(e.target.value)}
                    rows={3}
                    className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm bg-paper outline-none focus:border-line resize-none"
                  />
                </div>

                <div className="flex gap-2">
                  <button onClick={handleSolicitarEmprestimoClube} disabled={enviandoEmprestimo}
                    className="flex-1 py-3 text-sm bg-brand text-white rounded-xl hover:bg-brand-dark transition-colors disabled:opacity-50">
                    {enviandoEmprestimo ? 'Enviando...' : 'Solicitar empréstimo'}
                  </button>
                  <button onClick={() => setModalEmprestimo(null)}
                    className="flex-1 py-3 text-sm border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">
                    Cancelar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal: Founder saindo — também no detalhe mobile */}
      {modalSairFounder && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center lg:px-4" style={{background:'rgba(0,0,0,0.4)'}}>
          <div className="bg-white w-full max-w-md rounded-t-2xl lg:rounded-2xl p-6">
            <p className="text-base font-bold text-ink-900 mb-1">Você é o Founder</p>
            <p className="text-sm text-ink-400 mb-5">Ao sair, o admin mais antigo assumirá como novo Founder. Se não houver outro admin, o membro mais antigo será promovido.</p>
            <div className="flex flex-col gap-2">
              <button onClick={handleSairFounder} className="w-full py-3 text-sm font-medium text-ink-700 border border-line rounded-xl hover:bg-paper transition-colors">Sair do clube</button>
              <button onClick={() => { setModalSairFounder(false); handleDeletarClube(); }} className="w-full py-3 text-sm font-medium text-red-500 border border-red-100 rounded-xl hover:bg-red-50 transition-colors">Sair e excluir o clube</button>
              <button onClick={() => setModalSairFounder(false)} className="w-full py-3 text-sm text-ink-400 hover:text-ink-500 transition-colors">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Aviso ao clube — também no detalhe mobile */}
      {modalAviso && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center lg:px-4" style={{background:'rgba(0,0,0,0.4)'}}>
          <div className="bg-white w-full max-w-md rounded-t-2xl lg:rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-bold text-ink-900">Enviar aviso ao clube</p>
              <button onClick={() => setModalAviso(false)}><X size={18} strokeWidth={2} color="#9A93A4" /></button>
            </div>
            <p className="text-xs text-ink-400 mb-3">Todos os membros receberão uma notificação com este aviso.</p>
            <textarea placeholder="Ex: Discussão do cap. 10 no sábado às 19h!" value={textoAviso} onChange={e => setTextoAviso(e.target.value)} rows={4} className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm outline-none focus:border-line resize-none mb-4 text-ink-900" />
            <div className="flex gap-2">
              <button onClick={handleEnviarAviso} disabled={!textoAviso.trim() || enviandoAviso} className="flex-1 py-3 text-sm font-bold text-white rounded-xl border-0 cursor-pointer disabled:opacity-50" style={{background:'#5B3CE4'}}>{enviandoAviso ? 'Enviando...' : 'Enviar aviso'}</button>
              <button onClick={() => setModalAviso(false)} className="flex-1 py-3 text-sm border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      </>
    );
  }

  // ── LISTA ────────────────────────────────────────────────
  const publicosFiltrados = sortClubes(
    clubesPublicos.filter(c => !busca || c.nome?.toLowerCase().includes(busca) || c.descricao?.toLowerCase().includes(busca)),
    ordenacao
  );
  const meusClubIds = new Set(entradas.map(e => e.clube_id).filter(Boolean));
  const resultadosBusca = busca ? [
    ...entradas
      .filter(e => e.clubes?.nome?.toLowerCase().includes(busca) || e.clubes?.descricao?.toLowerCase().includes(busca))
      .map(e => ({ clube: e.clubes, entrada: e, isMembro: true })),
    ...clubesPublicos
      .filter(c => !meusClubIds.has(c.id) && (c.nome?.toLowerCase().includes(busca) || c.descricao?.toLowerCase().includes(busca)))
      .map(c => ({ clube: c, entrada: null, isMembro: false })),
  ] : [];

  const cardShadow = '0 1px 2px rgba(33,28,43,0.04),0 4px 16px rgba(33,28,43,0.05)';

  const renderClubCard = (clube, entrada, isPublico) => {
    const badgeLabel = !isPublico && clube.criador_id === userId ? 'Founder'
      : !isPublico && entrada?.role === 'admin' ? 'Admin'
      : !isPublico && entrada?.status === 'pendente' ? 'Pendente'
      : !isPublico ? 'Membro' : null;
    const membros = membrosMap[clube.id] || [];
    const criador = criadoresMap[clube.criador_id];
    const isGrade = visualizacao === 'grade';

    return (
      <div key={isPublico ? clube.id : entrada.id}
        className="bg-white border border-line overflow-hidden cursor-pointer"
        style={{borderRadius: isGrade ? 16 : 18, boxShadow: cardShadow}}
        onClick={() => isPublico ? navigate(`/clube/${clube.slug}`) : abrirDetalhe(entrada)}>
        <ClubBanner clubeId={clube.id} nome={clube.nome} fotoUrl={clube.foto_url} height={isGrade ? 80 : 64}>
          <p className="text-white font-extrabold m-0 tracking-tight relative z-10 leading-tight"
            style={{fontSize: isGrade ? 14 : 15, paddingRight: isGrade ? 44 : 56}}>
            {clube.nome}
          </p>
        </ClubBanner>
        <div style={{padding: isGrade ? '10px 12px 12px' : '10px 13px 12px'}}>
          {criador && (
            <p className="text-[11px] font-semibold m-0 mb-1.5" style={{color:'#9A93A4'}}>
              por {criador.nome || criador.username}
            </p>
          )}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <MemberBubbles membros={membros} />
              {badgeLabel && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5"
                  style={badgeLabel === 'Founder' ? {background:'#5B3CE4', color:'#fff'} : badgeLabel === 'Admin' ? {background:'#211C2B', color:'#fff'} : badgeLabel === 'Membro' ? {background:'#E4F6EE', color:'#1FA971'} : {background:'#FBF1DC', color:'#9A6B12'}}>
                  {badgeLabel === 'Founder' && <Crown size={8} strokeWidth={2.5} />}
                  {badgeLabel}
                </span>
              )}
              {clube.publico === false && !badgeLabel && (
                <Lock size={11} color="#9A93A4" strokeWidth={2} />
              )}
            </div>
            {!isGrade && (
              <button
                className="py-2 px-4 text-[12px] font-bold rounded-[11px] border-0 cursor-pointer flex-shrink-0"
                style={isPublico
                  ? {background:'#5B3CE4', color:'#fff', boxShadow:'0 4px 12px rgba(91,60,228,0.25)'}
                  : {background:'#F3EFE9', color:'#3D3748'}}
                onClick={e => { e.stopPropagation(); isPublico ? navigate(`/clube/${clube.slug}`) : abrirDetalhe(entrada); }}>
                {isPublico ? 'Ver' : '→'}
              </button>
            )}
          </div>
          {isGrade && (
            <button
              className="w-full mt-2.5 py-2 text-[12px] font-bold rounded-[11px] border-0 cursor-pointer"
              style={isPublico
                ? {background:'#5B3CE4', color:'#fff', boxShadow:'0 4px 12px rgba(91,60,228,0.25)'}
                : {background:'#F3EFE9', color:'#3D3748'}}
              onClick={e => { e.stopPropagation(); isPublico ? navigate(`/clube/${clube.slug}`) : abrirDetalhe(entrada); }}>
              {isPublico ? 'Ver clube' : 'Abrir →'}
            </button>
          )}
        </div>
      </div>
    );
  };

  const gridClass = visualizacao === 'grade'
    ? 'px-5 pt-3 grid grid-cols-2 gap-3'
    : 'px-5 pt-3 flex flex-col gap-3';

  return (
    <div className="bg-paper font-sans flex flex-col" style={{height:'100dvh'}} onClick={() => menuOrdem && setMenuOrdem(false)}>
      {mostrarIntro && <IntroCarousel slides={SLIDES_CLUBES} onClose={() => { localStorage.setItem(`tour_clubes_${userId}`, 'done'); setMostrarIntro(false); }} />}
      <div className="flex-1 overflow-y-auto lg:hidden" style={{scrollbarWidth:'none'}} onClick={() => menuOrdem && setMenuOrdem(false)}>
      <div className="max-w-md mx-auto pb-[104px]" style={{paddingTop:'env(safe-area-inset-top)'}}>

        {/* Cabeçalho */}
        <div className="flex items-center gap-3 px-5 pt-4 pb-0">
          <h1 className="flex-1 text-[24px] font-extrabold tracking-tight m-0 text-ink-900">Clubes do Livro</h1>
          <button onClick={() => setTela('criar')}
            className="w-10 h-10 rounded-[13px] bg-white border border-line flex items-center justify-center flex-shrink-0"
            style={{color:'#5B3CE4'}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
        </div>

        {/* Segmented tabs */}
        <div className="px-5 pt-3.5 pb-0">
          <div className="flex bg-paper-2 rounded-[11px] p-[3px] gap-0.5">
            {[['meus','Meus clubes'],['descobrir','Descobrir']].map(([k, l]) => (
              <button key={k}
                onClick={() => { setAbaLista(k); if (k === 'descobrir') buscarClubesPublicos(userId, entradas); }}
                className="flex-1 py-2 text-[13px] font-semibold rounded-[9px] border-0 cursor-pointer transition-all"
                style={abaLista === k
                  ? {background:'#5B3CE4', color:'#fff', boxShadow:'0 2px 6px rgba(91,60,228,0.25)'}
                  : {background:'transparent', color:'#9A93A4'}}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Toolbar: busca + ordenação + visualização */}
        <div className="px-5 pt-3 flex items-center gap-2">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9A93A4" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input type="text" placeholder="Buscar clubes…" value={buscaAtiva}
              onChange={e => { setBuscaAtiva(e.target.value); if (e.target.value.trim() && clubesPublicos.length === 0 && !carregandoPublicos) buscarClubesPublicos(userId, entradas); }}
              className="w-full pl-9 pr-3 py-2 text-[13px] font-medium bg-white border border-line rounded-[10px] outline-none text-ink-900"
            />
          </div>
          {/* Ordenação */}
          <div className="relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setMenuOrdem(v => !v)}
              className="w-9 h-9 rounded-[10px] bg-white border border-line flex items-center justify-center flex-shrink-0"
              style={{color: menuOrdem ? '#5B3CE4' : '#6B6478'}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M3 6h18M7 12h10M11 18h2"/></svg>
            </button>
            {menuOrdem && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-line rounded-[12px] overflow-hidden z-30 w-36"
                style={{boxShadow:'0 8px 20px rgba(0,0,0,0.12)'}}>
                {[['recente','Recente'],['nome','Nome A–Z']].map(([k, l]) => (
                  <button key={k} onClick={() => { setOrdenacao(k); setMenuOrdem(false); }}
                    className="w-full text-left px-3 py-2.5 text-[13px] font-semibold border-0 cursor-pointer"
                    style={ordenacao === k ? {color:'#5B3CE4', background:'#F0ECFD'} : {color:'#3D3748', background:'transparent'}}>
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Visualização */}
          <button onClick={() => setVisualizacao(v => { const n = v === 'lista' ? 'grade' : 'lista'; localStorage.setItem('clubes_visualizacao', n); return n; })}
            className="w-9 h-9 rounded-[10px] bg-white border border-line flex items-center justify-center flex-shrink-0"
            style={{color:'#6B6478'}}>
            {visualizacao === 'lista'
              ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
            }
          </button>
        </div>

        {carregando ? (
          <p className="text-sm text-ink-400 text-center mt-12">Carregando...</p>
        ) : busca ? (
          <div className={gridClass}>
            {carregandoPublicos ? (
              <p className="text-[13px] text-ink-400 text-center py-8 font-medium col-span-2">Buscando clubes…</p>
            ) : resultadosBusca.length === 0 ? (
              <div className="text-center mt-12 col-span-2">
                <Building2 size={40} color="#BDB7C5" strokeWidth={1.5} className="mx-auto mb-4" />
                <p className="text-[14px] font-bold text-ink-900 mb-1">Nenhum clube encontrado</p>
              </div>
            ) : (
              resultadosBusca.map(r => renderClubCard(r.clube, r.entrada, !r.isMembro))
            )}
          </div>
        ) : abaLista === 'meus' ? (
          <div className={gridClass}>
            {entradasFiltradas.length === 0 ? (
              <div className="text-center mt-12 col-span-2">
                <Building2 size={40} color="#BDB7C5" strokeWidth={1.5} className="mx-auto mb-4" />
                <p className="text-[14px] font-bold text-ink-900 mb-1">Você ainda não é membro de nenhum clube</p>
                <p className="text-[12.5px] text-ink-400 mb-6">Crie um clube ou explore a aba Descobrir</p>
                <div className="flex gap-2 justify-center">
                  <button onClick={() => setTela('criar')}
                    className="text-[13px] font-bold text-white px-5 py-2.5 rounded-[13px] border-0 cursor-pointer"
                    style={{background:'#5B3CE4', boxShadow:'0 6px 16px rgba(91,60,228,0.28)'}}>
                    + Criar clube
                  </button>
                  <button onClick={() => setAbaLista('descobrir')}
                    className="text-[13px] font-bold text-ink-700 px-5 py-2.5 rounded-[13px] border border-line bg-white cursor-pointer">
                    Descobrir
                  </button>
                </div>
              </div>
            ) : (
              entradasFiltradas.map(entrada => {
                const clube = entrada.clubes;
                if (!clube) return null;
                return renderClubCard(clube, entrada, false);
              })
            )}
          </div>
        ) : (
          <div className={gridClass}>
            {carregandoPublicos ? (
              <p className="text-[13px] text-ink-400 text-center py-8 font-medium col-span-2">Buscando clubes…</p>
            ) : publicosFiltrados.length === 0 ? (
              <div className="text-center mt-12 col-span-2">
                <Building2 size={40} color="#BDB7C5" strokeWidth={1.5} className="mx-auto mb-4" />
                <p className="text-[14px] font-bold text-ink-900 mb-1">Nenhum clube público encontrado</p>
                <p className="text-[12.5px] text-ink-400">Seja o primeiro a criar um!</p>
              </div>
            ) : (
              publicosFiltrados.map(clube => renderClubCard(clube, null, true))
            )}
          </div>
        )}
      </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden lg:flex flex-1 overflow-hidden">
        {/* Left panel: club list */}
        <div style={{width:280,flexShrink:0,borderRight:'1px solid #EDE9F5',display:'flex',flexDirection:'column',height:'100%'}}>
          <div style={{padding:'28px 16px 12px'}}>
            <p style={{fontSize:10,fontWeight:800,letterSpacing:'.15em',textTransform:'uppercase',color:'#5B3CE4',margin:'0 0 4px'}}>CLUBES</p>
            <h1 style={{fontSize:22,fontWeight:800,color:'#211C2B',margin:'0 0 14px',lineHeight:1.1}}>Seus clubes de leitura</h1>
            <div style={{position:'relative'}}>
              <svg style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)'}} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9A93A4" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input type="text" placeholder="Buscar clube..." value={buscaAtiva}
                onChange={e => { setBuscaAtiva(e.target.value); if (e.target.value.trim() && clubesPublicos.length === 0 && !carregandoPublicos) buscarClubesPublicos(userId, entradas); }}
                style={{width:'100%',padding:'9px 9px 9px 30px',background:'#fff',border:'1px solid #EDE9F5',borderRadius:12,fontSize:13,outline:'none',boxSizing:'border-box'}} />
            </div>
          </div>
          <div style={{flex:1,overflowY:'auto',scrollbarWidth:'none'}}>
            {busca ? (
              carregandoPublicos ? (
                <p style={{fontSize:13,color:'#9A93A4',textAlign:'center',padding:'24px 16px'}}>Buscando…</p>
              ) : resultadosBusca.length === 0 ? (
                <p style={{fontSize:13,color:'#9A93A4',textAlign:'center',padding:'24px 16px'}}>Nenhum clube encontrado</p>
              ) : resultadosBusca.map(r => {
                const clube = r.clube;
                if (!clube) return null;
                const [g1, g2] = getClubGradient(clube.id);
                const isSelected = clubeAtivo?.id === clube.id;
                const memCount = (membrosMap[clube.id] || []).length;
                return (
                  <div key={clube.id}
                    onClick={() => r.isMembro ? selecionarClubeDesktop(r.entrada) : navigate(`/clube/${clube.slug}`)}
                    style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',cursor:'pointer',background:isSelected?'#EDE9F5':'transparent',transition:'background 0.15s'}}>
                    <div style={{width:42,height:42,borderRadius:12,background:`linear-gradient(135deg,${g1},${g2})`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:17,fontWeight:800,color:'#fff',overflow:'hidden'}}>
                      {clube.foto_url ? <img src={clube.foto_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} /> : (clube.nome||'?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontWeight:700,fontSize:14,color:'#211C2B',margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{clube.nome}</p>
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <p style={{fontSize:12,color:'#9A93A4',margin:0}}>{memCount} {memCount===1?'membro':'membros'}</p>
                        {r.isMembro && <span style={{fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:4,background:'#E4F6EE',color:'#1FA971'}}>Membro</span>}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : entradasFiltradas.map(entrada => {
              const clube = entrada.clubes;
              if (!clube) return null;
              const [g1, g2] = getClubGradient(clube.id);
              const isSelected = clubeAtivo?.id === clube.id;
              const memCount = (membrosMap[clube.id] || []).length;
              return (
                <div key={clube.id} onClick={() => selecionarClubeDesktop(entrada)}
                  style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',cursor:'pointer',background:isSelected?'#EDE9F5':'transparent',transition:'background 0.15s'}}>
                  <div style={{width:42,height:42,borderRadius:12,background:`linear-gradient(135deg,${g1},${g2})`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:17,fontWeight:800,color:'#fff',overflow:'hidden'}}>
                    {clube.foto_url ? <img src={clube.foto_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} /> : (clube.nome||'?').charAt(0).toUpperCase()}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontWeight:700,fontSize:14,color:'#211C2B',margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{clube.nome}</p>
                    <p style={{fontSize:12,color:'#9A93A4',margin:0}}>{memCount} {memCount===1?'membro':'membros'}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{padding:'12px 16px',borderTop:'1px solid #EDE9F5'}}>
            <button onClick={() => { setNome(''); setDescricao(''); setPrivado(false); setCorIndex(0); setModalCriarAberto(true); }}
              style={{width:'100%',padding:'10px',background:'#5B3CE4',border:'none',borderRadius:12,fontSize:13,fontWeight:700,color:'#fff',cursor:'pointer'}}>
              + Criar clube
            </button>
          </div>
        </div>

        {/* Right panel: club detail */}
        <div style={{flex:1,overflowY:'auto',scrollbarWidth:'none',background:'#F6F5FB'}}>
          {!clubeAtivo ? (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}>
              <p style={{color:'#9A93A4',fontSize:14}}>Selecione um clube para ver os detalhes</p>
            </div>
          ) : carregandoDetalhe ? (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}>
              <p style={{color:'#9A93A4',fontSize:14}}>Carregando...</p>
            </div>
          ) : (
            <div>
            {/* Hero com capa + info sobrepostos */}
            {clubeAtivo.capa_url ? (() => {
              const [g1,g2] = getClubGradient(clubeAtivo.id);
              const dIsFounder = clubeAtivo.criador_id === userId;
              return (
                <div style={{position:'relative',width:'100%',height:260,overflow:'hidden',flexShrink:0}}>
                  <img src={clubeAtivo.capa_url} alt="" style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}} />
                  {/* card glassmorphism na base */}
                  <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'20px 32px',display:'flex',alignItems:'center',gap:20,
                    background:'rgba(0,0,0,0.32)',
                    borderTop:'1px solid rgba(255,255,255,0.12)'}}>
                    <div style={{width:72,height:72,borderRadius:18,background:`linear-gradient(135deg,${g1},${g2})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,fontWeight:800,color:'#fff',flexShrink:0,overflow:'hidden',boxShadow:'0 4px 16px rgba(0,0,0,0.18)'}}>
                      {clubeAtivo.foto_url ? <img src={clubeAtivo.foto_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} /> : (clubeAtivo.nome||'?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <h2 style={{fontSize:22,fontWeight:800,color:'#fff',margin:'0 0 3px',lineHeight:1.1,textShadow:'0 1px 4px rgba(0,0,0,0.25)'}}>{clubeAtivo.nome}</h2>
                      <p style={{fontSize:12,color:'rgba(255,255,255,0.8)',margin:'0 0 8px',textShadow:'0 1px 3px rgba(0,0,0,0.2)'}}>
                        {membros.filter(m=>m.status==='aprovado').length} membros · {clubeAtivo.publico!==false?'público':'privado'} · criado por {criadoresMap[clubeAtivo.criador_id]?.nome||''}
                      </p>
                      <MemberBubbles membros={membros.filter(m=>m.status==='aprovado').slice(0,6).map(m=>m.perfil||{nome:null,foto_url:null})} />
                    </div>
                    <div style={{display:'flex',gap:8,flexShrink:0,alignItems:'center'}}>
                      {dIsFounder && (
                        <button onClick={abrirConfiguracoes} title="Configurações do clube"
                          style={{width:38,height:38,borderRadius:999,background:'rgba(255,255,255,0.25)',backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,0.4)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff'}}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                        </button>
                      )}
                      <button onClick={dIsFounder ? () => setModalSairFounder(true) : handleSairDoClube}
                        style={{padding:'9px 16px',borderRadius:999,background:'rgba(255,255,255,0.2)',backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,0.35)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer'}}>
                        Sair do clube
                      </button>
                      <button onClick={abrirModalConvidar}
                        style={{padding:'9px 20px',borderRadius:999,background:'#5B3CE4',color:'#fff',fontWeight:700,fontSize:13,border:'none',cursor:'pointer',boxShadow:'0 2px 12px rgba(91,60,228,0.4)'}}>
                        Convidar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })() : (() => {
              const [g1,g2] = getClubGradient(clubeAtivo.id);
              const dIsFounder = clubeAtivo.criador_id === userId;
              return (
                <div style={{padding:'32px 40px 0'}}>
                  <div style={{display:'flex',alignItems:'flex-start',gap:20,marginBottom:24}}>
                    <div style={{width:80,height:80,borderRadius:20,background:`linear-gradient(135deg,${g1},${g2})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:30,fontWeight:800,color:'#fff',flexShrink:0,overflow:'hidden'}}>
                      {clubeAtivo.foto_url ? <img src={clubeAtivo.foto_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} /> : (clubeAtivo.nome||'?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{flex:1}}>
                      <h2 style={{fontSize:26,fontWeight:800,color:'#211C2B',margin:'0 0 4px',lineHeight:1.1}}>{clubeAtivo.nome}</h2>
                      <p style={{fontSize:13,color:'#9A93A4',margin:'0 0 12px'}}>
                        {membros.filter(m=>m.status==='aprovado').length} membros · {clubeAtivo.publico!==false?'público':'privado'} · criado por {criadoresMap[clubeAtivo.criador_id]?.nome||''}
                      </p>
                      <MemberBubbles membros={membros.filter(m=>m.status==='aprovado').slice(0,6).map(m=>m.perfil||{nome:null,foto_url:null})} />
                    </div>
                    <div style={{display:'flex',gap:8,flexShrink:0,alignItems:'center'}}>
                      {dIsFounder && (
                        <button onClick={abrirConfiguracoes} title="Configurações do clube"
                          style={{width:38,height:38,borderRadius:999,background:'#F2EEFE',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#5B3CE4'}}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                        </button>
                      )}
                      <button onClick={dIsFounder ? () => setModalSairFounder(true) : handleSairDoClube}
                        style={{padding:'10px 16px',borderRadius:999,background:'#FFF5F3',color:'#E55B3C',fontWeight:700,fontSize:13,border:'none',cursor:'pointer'}}>
                        Sair do clube
                      </button>
                      <button onClick={abrirModalConvidar}
                        style={{padding:'10px 20px',borderRadius:999,background:'#5B3CE4',color:'#fff',fontWeight:700,fontSize:13,border:'none',cursor:'pointer'}}>
                        Convidar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
            <div style={{padding:'0 40px 32px'}}>
              <div style={{display:'flex',gap:2,borderBottom:'2px solid #EDE9F5',marginBottom:24,marginTop: clubeAtivo.capa_url ? 20 : 0}}>
                {[['estante','Estante'],['membros','Membros'],['atividade','Atividade']].map(([k,l]) => (
                  <button key={k} onClick={() => setDesktopAba(k)}
                    style={{padding:'8px 20px',border:'none',background:'transparent',cursor:'pointer',fontWeight:700,fontSize:14,color:desktopAba===k?'#5B3CE4':'#9A93A4',borderBottom:desktopAba===k?'2px solid #5B3CE4':'2px solid transparent',marginBottom:-2}}>
                    {l}
                  </button>
                ))}
              </div>
              {desktopAba==='estante' && (() => {
                const dIsFounder = clubeAtivo.criador_id === userId;
                const dIsAdmin = clubeAtivo.meuRole === 'admin' || dIsFounder;
                const bgs = ['linear-gradient(160deg,#2D3B55,#1A2D45)','linear-gradient(160deg,#3D6B2E,#1E4A1A)','linear-gradient(160deg,#6E3030,#3D1A1A)','linear-gradient(160deg,#4A3B6E,#2D1A55)'];
                return (
                  <>
                    {livrosClube.length===0 ? (
                      <div style={{textAlign:'center',padding:'48px 24px'}}>
                        <div style={{width:64,height:64,borderRadius:20,background:'#EDE9FF',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px'}}>
                          <BookOpen size={28} color="#5B3CE4" strokeWidth={1.5} />
                        </div>
                        <p style={{fontSize:16,fontWeight:700,color:'#211C2B',margin:'0 0 6px'}}>A estante está vazia</p>
                        <p style={{fontSize:13,color:'#9A93A4',margin:'0 0 24px',lineHeight:1.5}}>
                          {clubeAtivo.meuStatus==='aprovado'
                            ? 'Adicione um livro do seu acervo para começar a compartilhar com o grupo.'
                            : 'Nenhum membro adicionou livros ainda.'}
                        </p>
                        {clubeAtivo.meuStatus==='aprovado' && (
                          <button onClick={()=>{setModalContribuicao(true);setEtapaContribuicao('opcao');setLivrosSelecionados(new Set());}}
                            style={{padding:'12px 28px',borderRadius:12,background:'#5B3CE4',color:'#fff',fontWeight:700,fontSize:13,border:'none',cursor:'pointer',display:'inline-block'}}>
                            Adicionar livro ao clube
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <h3 style={{fontSize:14,fontWeight:700,color:'#9A93A4',textTransform:'uppercase',letterSpacing:'.08em',margin:'0 0 16px'}}>Lendo agora</h3>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24}}>
                          {livrosClube.map(cl => {
                            const livro = cl.livros;
                            if (!livro) return null;
                            return (
                              <div key={cl.id} style={{aspectRatio:'2/3',borderRadius:12,overflow:'hidden',position:'relative',background:livro.capa_url?'#ccc':bgs[livro.titulo?.charCodeAt(0)%bgs.length||0]}}>
                                {livro.capa_url
                                  ? <img src={livro.capa_url} alt={livro.titulo} style={{width:'100%',height:'100%',objectFit:'cover'}} />
                                  : <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',justifyContent:'flex-end',padding:'8px 10px',background:'linear-gradient(0deg,rgba(0,0,0,0.65),transparent)'}}>
                                      <p style={{color:'#fff',fontWeight:700,fontSize:12,margin:0}}>{livro.titulo}</p>
                                      <p style={{color:'rgba(255,255,255,0.65)',fontSize:10,margin:0}}>{livro.autor}</p>
                                    </div>
                                }
                              </div>
                            );
                          })}
                        </div>
                        {clubeAtivo.meuStatus==='aprovado' && (
                          <div style={{display:'flex',gap:10}}>
                            <button onClick={()=>{setModalContribuicao(true);setEtapaContribuicao('opcao');setLivrosSelecionados(new Set());}}
                              style={{padding:'10px 18px',borderRadius:12,background:'#5B3CE4',color:'#fff',fontWeight:700,fontSize:13,border:'none',cursor:'pointer'}}>
                              Adicionar livro
                            </button>
                            {dIsAdmin && (
                              <button onClick={()=>{setTextoAviso('');setModalAviso(true);}}
                                style={{padding:'10px 18px',borderRadius:12,background:'#F0ECFE',color:'#5B3CE4',fontWeight:700,fontSize:13,border:'none',cursor:'pointer'}}>
                                Enviar aviso
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {livrosClube.length===0 && dIsAdmin && clubeAtivo.meuStatus==='aprovado' && (
                      <div style={{textAlign:'center',marginTop:12}}>
                        <button onClick={()=>{setTextoAviso('');setModalAviso(true);}}
                          style={{padding:'10px 18px',borderRadius:12,background:'#F0ECFE',color:'#5B3CE4',fontWeight:700,fontSize:13,border:'none',cursor:'pointer'}}>
                          Enviar aviso ao clube
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
              {desktopAba==='membros' && (() => {
                const dIsFounder = clubeAtivo.criador_id === userId;
                const dIsAdmin = clubeAtivo.meuRole === 'admin' || dIsFounder;
                const dPendentes = membros.filter(m => m.status === 'pendente');
                const dAprovados = membros.filter(m => m.status === 'aprovado');
                return (
                  <div style={{display:'flex',flexDirection:'column',gap:20}}>
                    {membroMenuId && <div style={{position:'fixed',inset:0,zIndex:10}} onClick={() => setMembroMenuId(null)} />}
                    {dIsAdmin && dPendentes.length > 0 && (
                      <div>
                        <p style={{fontSize:13,fontWeight:700,color:'#211C2B',margin:'0 0 10px'}}>
                          Pedidos de entrada
                          <span style={{marginLeft:8,fontSize:11,fontWeight:800,background:'#211C2B',color:'#fff',padding:'2px 7px',borderRadius:999}}>{dPendentes.length}</span>
                        </p>
                        {dPendentes.map(m => (
                          <div key={m.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'#fff',borderRadius:12,boxShadow:'0 1px 3px rgba(33,28,43,0.06)',marginBottom:6}}>
                            <div style={{width:36,height:36,borderRadius:'50%',background:'#EDE9FF',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,color:'#5B3CE4',flexShrink:0}}>{(m.perfil?.nome||'?').charAt(0).toUpperCase()}</div>
                            <div style={{flex:1,minWidth:0}}>
                              <p style={{fontWeight:600,fontSize:13,color:'#211C2B',margin:0}}>{m.perfil?.nome||'Usuário'}</p>
                              {m.perfil?.username&&<p style={{fontSize:11,color:'#9A93A4',margin:'1px 0 0'}}>@{m.perfil.username}</p>}
                            </div>
                            <div style={{display:'flex',gap:8}}>
                              <button onClick={()=>handleAprovar(m.id)} style={{padding:'6px 14px',background:'#5B3CE4',color:'#fff',fontSize:12,fontWeight:700,border:'none',borderRadius:8,cursor:'pointer'}}>Aprovar</button>
                              <button onClick={()=>handleRejeitar(m.id)} style={{padding:'6px 14px',background:'transparent',color:'#9A93A4',fontSize:12,fontWeight:600,border:'1px solid #EDE9F5',borderRadius:8,cursor:'pointer'}}>Recusar</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div>
                      <p style={{fontSize:13,fontWeight:700,color:'#211C2B',margin:'0 0 10px'}}>Membros ({dAprovados.length})</p>
                      <div style={{display:'flex',flexDirection:'column',gap:6}}>
                        {dAprovados.map(m => {
                          const isMemberFounder = m.user_id === clubeAtivo.criador_id;
                          const isMe = m.user_id === userId;
                          const showAdminMenu = dIsAdmin && !isMemberFounder && !isMe;
                          const showSelfMenu = dIsAdmin && !dIsFounder && isMe;
                          return (
                            <div key={m.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'#fff',borderRadius:12,boxShadow:'0 1px 3px rgba(33,28,43,0.06)',position:'relative',zIndex:1}}>
                              <div style={{width:36,height:36,borderRadius:'50%',background:getAvatarColor(m.perfil?.nome),display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,color:'#fff',flexShrink:0,overflow:'hidden'}}>
                                {m.perfil?.foto_url?<img src={m.perfil.foto_url} style={{width:'100%',height:'100%',objectFit:'cover'}} alt="" />:(m.perfil?.nome||'?').charAt(0).toUpperCase()}
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                <p style={{fontWeight:600,fontSize:13,color:'#211C2B',margin:0}}>{m.perfil?.nome||'Usuário'}</p>
                                {m.perfil?.username&&<p style={{fontSize:11,color:'#9A93A4',margin:'1px 0 0'}}>@{m.perfil.username}</p>}
                              </div>
                              <span style={{fontSize:11,fontWeight:700,padding:'3px 8px',borderRadius:999,display:'flex',alignItems:'center',gap:4,flexShrink:0,
                                background:isMemberFounder?'#5B3CE4':m.role==='admin'?'#211C2B':'#F2EEFE',
                                color:isMemberFounder?'#fff':m.role==='admin'?'#fff':'#5B3CE4'}}>
                                {isMemberFounder&&<Crown size={9} strokeWidth={2.5}/>}
                                {isMemberFounder?'Founder':m.role==='admin'?'Admin':'Membro'}
                              </span>
                              {(showAdminMenu||showSelfMenu) && (
                                <div style={{position:'relative',flexShrink:0,zIndex:20}}>
                                  <button onClick={e=>{e.stopPropagation();setMembroMenuId(membroMenuId===m.id?null:m.id);}}
                                    style={{width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center',background:'transparent',border:'none',cursor:'pointer',color:'#C4BECC',borderRadius:'50%'}}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                                  </button>
                                  {membroMenuId===m.id&&(
                                    <div style={{position:'absolute',right:0,top:32,background:'#fff',border:'1px solid #EDE9F5',borderRadius:12,boxShadow:'0 4px 16px rgba(0,0,0,.12)',zIndex:30,minWidth:170,overflow:'hidden'}}>
                                      {showAdminMenu&&m.role!=='admin'&&<button onClick={()=>handlePromoverAdmin(m.id)} style={{width:'100%',textAlign:'left',padding:'10px 14px',fontSize:12,color:'#211C2B',border:'none',background:'transparent',cursor:'pointer',display:'block'}}>Promover a admin</button>}
                                      {showAdminMenu&&m.role==='admin'&&<button onClick={()=>handleDemoverAdmin(m.id)} style={{width:'100%',textAlign:'left',padding:'10px 14px',fontSize:12,color:'#211C2B',border:'none',background:'transparent',cursor:'pointer',display:'block'}}>Rebaixar para membro</button>}
                                      {showAdminMenu&&<button onClick={()=>handleRemoverMembro(m.id)} style={{width:'100%',textAlign:'left',padding:'10px 14px',fontSize:12,color:'#E55B3C',border:'none',background:'transparent',cursor:'pointer',display:'block'}}>Remover do clube</button>}
                                      {showAdminMenu&&<button onClick={()=>handleBloquearMembro(m.id)} style={{width:'100%',textAlign:'left',padding:'10px 14px',fontSize:12,color:'#E55B3C',border:'none',background:'transparent',cursor:'pointer',display:'block'}}>Bloquear</button>}
                                      {showSelfMenu&&<button onClick={()=>handleDemoverAdmin(m.id)} style={{width:'100%',textAlign:'left',padding:'10px 14px',fontSize:12,color:'#211C2B',border:'none',background:'transparent',cursor:'pointer',display:'block'}}>Deixar de ser admin</button>}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}
              {desktopAba==='atividade' && (
                <>
                  <h3 style={{fontSize:16,fontWeight:800,color:'#211C2B',margin:'0 0 16px'}}>Atividade recente</h3>
                  {feedAtividade.length===0 ? (
                    <p style={{color:'#9A93A4',fontSize:14}}>Nenhuma atividade ainda.</p>
                  ) : (
                    <div style={{display:'flex',flexDirection:'column',gap:12}}>
                      {feedAtividade.map((evt,i) => (
                        <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:'#fff',borderRadius:12,boxShadow:'0 1px 3px rgba(33,28,43,0.06)'}}>
                          <div style={{width:36,height:36,borderRadius:'50%',background:getAvatarColor(evt.nome),display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,color:'#fff',flexShrink:0}}>
                            {(evt.nome||'?').charAt(0).toUpperCase()}
                          </div>
                          <p style={{fontSize:13,color:'#211C2B',margin:0,flex:1}}>
                            <strong>{evt.nome}</strong>{evt.tipo==='entrou'?' entrou no clube':<> adicionou <em>"{evt.livro}"</em></>}
                          </p>
                          <span style={{fontSize:11,color:'#9A93A4',flexShrink:0}}>{tempoRelativo(evt.ts)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom nav — mobile only */}
      <div className="fixed bottom-0 left-0 right-0 z-30 flex items-center px-[18px] lg:hidden"
        style={{ paddingTop: 10, paddingBottom: 'calc(10px + env(safe-area-inset-bottom))', background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(18px) saturate(160%)', WebkitBackdropFilter: 'blur(18px) saturate(160%)', borderTop: '1px solid #F2EEE9' }}>
        <button onClick={() => setTelaApp('acervo')}
          className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#9A93A4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/><path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/>
            <line x1="3" y1="6" x2="3" y2="19"/><line x1="12" y1="6" x2="12" y2="19"/><line x1="21" y1="6" x2="21" y2="19"/>
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Estante</span>
        </button>
        <button onClick={() => setTelaApp('explorar')}
          className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#9A93A4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Explorar</span>
        </button>
        <div className="flex-none mx-1.5">
          <button onClick={() => setTelaApp('cadastrar')}
            className="w-[52px] h-[52px] rounded-[17px] border-0 cursor-pointer text-white flex items-center justify-center"
            style={{ background: 'linear-gradient(150deg,#6E50F0,#3F27B0)', marginTop: -18, boxShadow: '0 8px 18px rgba(91,60,228,0.40)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </button>
        </div>
        <button onClick={() => setTelaApp('amigos')}
          className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#9A93A4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <polyline points="16 11 18 13 22 9"/>
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Amigos</span>
        </button>
        <button className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#5B3CE4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Clubes</span>
        </button>
      </div>
      {/* Modal: Founder saindo do clube — desktop */}
      {modalSairFounder && (
        <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 16px',background:'rgba(0,0,0,0.4)'}}>
          <div style={{background:'#fff',borderRadius:24,width:'100%',maxWidth:420,padding:28}}>
            <p style={{fontSize:17,fontWeight:800,color:'#211C2B',margin:'0 0 6px'}}>Você é o Founder</p>
            <p style={{fontSize:13,color:'#9A93A4',margin:'0 0 20px',lineHeight:1.5}}>Ao sair, o admin mais antigo assumirá como novo Founder. Se não houver outro admin, o membro mais antigo será promovido.</p>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              <button onClick={handleSairFounder} style={{width:'100%',padding:13,borderRadius:12,background:'#F2EEFE',color:'#5B3CE4',fontWeight:700,fontSize:14,border:'none',cursor:'pointer'}}>Sair do clube</button>
              <button onClick={()=>{setModalSairFounder(false);handleDeletarClube();}} style={{width:'100%',padding:13,borderRadius:12,background:'#FFF5F3',color:'#E55B3C',fontWeight:700,fontSize:14,border:'none',cursor:'pointer'}}>Sair e excluir o clube</button>
              <button onClick={()=>setModalSairFounder(false)} style={{width:'100%',padding:13,borderRadius:12,background:'transparent',color:'#9A93A4',fontWeight:600,fontSize:14,border:'none',cursor:'pointer'}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Aviso ao clube — desktop */}
      {modalAviso && (
        <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 16px',background:'rgba(0,0,0,0.4)'}}>
          <div style={{background:'#fff',borderRadius:24,width:'100%',maxWidth:440,padding:28}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
              <p style={{fontSize:16,fontWeight:800,color:'#211C2B',margin:0}}>Enviar aviso ao clube</p>
              <button onClick={()=>setModalAviso(false)} style={{background:'none',border:'none',cursor:'pointer',padding:4}}><X size={20} color="#9A93A4" /></button>
            </div>
            <p style={{fontSize:12,color:'#9A93A4',margin:'0 0 12px'}}>Todos os membros receberão uma notificação com este aviso.</p>
            <textarea placeholder="Ex: Discussão do cap. 10 no sábado às 19h!" value={textoAviso} onChange={e=>setTextoAviso(e.target.value)} rows={4}
              style={{width:'100%',border:'1px solid #EDE9F5',borderRadius:12,padding:'12px 16px',fontSize:14,outline:'none',resize:'none',boxSizing:'border-box',marginBottom:16}} />
            <div style={{display:'flex',gap:10}}>
              <button onClick={handleEnviarAviso} disabled={!textoAviso.trim()||enviandoAviso} style={{flex:1,padding:13,borderRadius:12,background:!textoAviso.trim()||enviandoAviso?'#D4CFE2':'#5B3CE4',color:'#fff',fontWeight:700,fontSize:14,border:'none',cursor:'pointer'}}>
                {enviandoAviso?'Enviando...':'Enviar aviso'}
              </button>
              <button onClick={()=>setModalAviso(false)} style={{flex:1,padding:13,borderRadius:12,background:'transparent',color:'#9A93A4',fontWeight:600,fontSize:14,border:'1px solid #EDE9F5',cursor:'pointer'}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal criar clube — desktop only */}
      {modalCriarAberto && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 16px'}}
          onClick={() => setModalCriarAberto(false)}>
          <div style={{background:'#fff',borderRadius:24,width:'100%',maxWidth:480,padding:32,maxHeight:'90vh',overflowY:'auto'}}
            onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
              <h2 style={{fontSize:20,fontWeight:800,color:'#211C2B',margin:0}}>Criar clube</h2>
              <button onClick={() => setModalCriarAberto(false)} style={{background:'none',border:'none',cursor:'pointer',padding:4}}>
                <X size={20} color="#9A93A4" />
              </button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              <div>
                <label style={{fontSize:12,color:'#9A93A4',display:'block',marginBottom:4}}>Nome do clube *</label>
                <input type="text" value={nome} onChange={e => setNome(e.target.value)}
                  placeholder="Ex: Clube do livro da turma"
                  style={{width:'100%',border:'1px solid #EDE9F5',borderRadius:12,padding:'12px 16px',fontSize:14,color:'#211C2B',background:'#F9F7FF',outline:'none',boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{fontSize:12,color:'#9A93A4',display:'block',marginBottom:4}}>Descrição <span style={{color:'#C4BECC'}}>(opcional)</span></label>
                <textarea value={descricao} onChange={e => setDescricao(e.target.value)}
                  placeholder="Sobre o clube..." rows={3}
                  style={{width:'100%',border:'1px solid #EDE9F5',borderRadius:12,padding:'12px 16px',fontSize:14,color:'#211C2B',background:'#F9F7FF',outline:'none',resize:'none',boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{fontSize:12,color:'#9A93A4',display:'block',marginBottom:8}}>Foto de capa <span style={{color:'#C4BECC'}}>(opcional)</span></label>
                <input ref={novaCapaInputRef} type="file" accept="image/*" style={{display:'none'}}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setNovaCapaFile(f);
                    setNovaCapaPreview(URL.createObjectURL(f));
                  }} />
                {novaCapaPreview ? (
                  <div style={{position:'relative',borderRadius:12,overflow:'hidden',height:120}}>
                    <img src={novaCapaPreview} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                    <button type="button" onClick={() => { setNovaCapaFile(null); setNovaCapaPreview(null); }}
                      style={{position:'absolute',top:6,right:6,background:'rgba(0,0,0,0.5)',border:'none',borderRadius:'50%',width:24,height:24,cursor:'pointer',color:'#fff',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      ×
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => novaCapaInputRef.current?.click()}
                    style={{width:'100%',border:'1.5px dashed #D4CFE2',borderRadius:12,padding:'18px 0',fontSize:13,color:'#9A93A4',background:'#F9F7FF',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                    Escolher imagem
                  </button>
                )}
              </div>
              <div>
                <label style={{fontSize:12,color:'#9A93A4',display:'block',marginBottom:8}}>Foto do clube <span style={{color:'#C4BECC'}}>(opcional)</span></label>
                <input ref={novaFotoInputRef} type="file" accept="image/*" style={{display:'none'}}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setNovaFotoFile(f);
                    setNovaFotoPreview(URL.createObjectURL(f));
                  }} />
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <div onClick={() => novaFotoInputRef.current?.click()}
                    style={{width:64,height:64,borderRadius:16,background:novaFotoPreview?'#ccc':`linear-gradient(135deg,${CLUB_GRADIENTS[corIndex][0]},${CLUB_GRADIENTS[corIndex][1]})`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,overflow:'hidden',cursor:'pointer',border:'2px dashed rgba(0,0,0,0.08)'}}>
                    {novaFotoPreview
                      ? <img src={novaFotoPreview} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                      : <span style={{fontSize:22,fontWeight:800,color:'#fff'}}>{(nome||'C').charAt(0).toUpperCase()}</span>}
                  </div>
                  <div style={{flex:1}}>
                    <button type="button" onClick={() => novaFotoInputRef.current?.click()}
                      style={{fontSize:13,color:'#5B3CE4',fontWeight:600,background:'none',border:'none',cursor:'pointer',padding:0}}>
                      {novaFotoPreview ? 'Trocar foto' : 'Escolher foto'}
                    </button>
                    {novaFotoPreview && (
                      <button type="button" onClick={() => { setNovaFotoFile(null); setNovaFotoPreview(null); }}
                        style={{display:'block',fontSize:12,color:'#9A93A4',background:'none',border:'none',cursor:'pointer',padding:'4px 0 0',marginTop:2}}>
                        Remover
                      </button>
                    )}
                    <p style={{fontSize:11,color:'#C4BECC',margin:'4px 0 0'}}>Aparece no avatar do clube</p>
                  </div>
                </div>
              </div>
              <div>
                <label style={{fontSize:12,color:'#9A93A4',display:'block',marginBottom:8}}>Cor do clube</label>
                <div style={{display:'flex',gap:8}}>
                  {CLUB_GRADIENTS.map((g, i) => (
                    <button key={i} type="button" onClick={() => setCorIndex(i)}
                      style={{width:32,height:32,borderRadius:'50%',flexShrink:0,border:corIndex===i?'2px solid #5B3CE4':'2px solid transparent',background:`linear-gradient(135deg,${g[0]},${g[1]})`,cursor:'pointer'}} />
                  ))}
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div>
                  <p style={{fontSize:14,fontWeight:600,color:'#211C2B',margin:0}}>Clube privado</p>
                  <p style={{fontSize:12,color:'#9A93A4',margin:'2px 0 0'}}>Membros entram por convite</p>
                </div>
                <button type="button" onClick={() => setPrivado(v => !v)}
                  style={{position:'relative',width:44,height:24,borderRadius:999,border:'none',cursor:'pointer',background:privado?'#5B3CE4':'#ECE7E1',transition:'background .2s',flexShrink:0}}>
                  <span style={{position:'absolute',top:2,left:2,width:20,height:20,background:'#fff',borderRadius:'50%',boxShadow:'0 1px 3px rgba(0,0,0,.2)',transition:'transform .2s',transform:privado?'translateX(20px)':'translateX(0)'}} />
                </button>
              </div>
              {erroForm && <p style={{fontSize:12,color:'#E55B3C',margin:0}}>{erroForm}</p>}
              <button onClick={handleCriarClube} disabled={salvando}
                style={{width:'100%',padding:14,borderRadius:14,background:salvando?'#D4CFE2':'#5B3CE4',color:'#fff',fontSize:15,fontWeight:700,border:'none',cursor:salvando?'default':'pointer'}}>
                {salvando ? 'Criando...' : 'Criar clube'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ModalConvidar
        aberto={modalConvidar}
        onFechar={() => setModalConvidar(false)}
        amigos={amigosConvidar}
        selecionados={selecionadosConvidar}
        onToggle={id => setSelecionadosConvidar(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; })}
        carregando={carregandoAmigos}
        onEnviar={enviarConvites}
        enviando={enviandoConvite}
        onCopiarLink={clubeAtivo ? () => copiarLink(clubeAtivo.slug) : undefined}
      />
    </div>
  );
}

function ModalConvidar({ aberto, onFechar, amigos, selecionados, onToggle, carregando, onEnviar, enviando, onCopiarLink }) {
  if (!aberto) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0 0' }}
      className="lg:items-center lg:px-4"
      onClick={onFechar}>
      <div style={{ background: '#fff', width: '100%', maxWidth: 480, maxHeight: '85vh', display: 'flex', flexDirection: 'column', borderRadius: '24px 24px 0 0' }}
        className="lg:rounded-[24px]"
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 20px 12px', borderBottom: '1px solid #F2EEF9' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#211C2B', margin: 0 }}>Convidar para o clube</h2>
            <button onClick={onFechar} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={20} color="#9A93A4" />
            </button>
          </div>
        </div>

        {/* Share link section */}
        {onCopiarLink && (
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #F2EEF9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#211C2B', margin: 0 }}>Compartilhar link</p>
              <p style={{ fontSize: 12, color: '#9A93A4', margin: '2px 0 0' }}>Qualquer pessoa com o link pode solicitar entrada</p>
            </div>
            <button onClick={onCopiarLink}
              style={{ flexShrink: 0, padding: '8px 16px', borderRadius: 999, background: '#F0ECFE', color: '#5B3CE4', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
              Copiar link
            </button>
          </div>
        )}

        <div style={{ padding: '10px 20px 2px' }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#9A93A4', textTransform: 'uppercase', letterSpacing: '.1em', margin: 0 }}>Convidar pelo app</p>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {carregando ? (
            <p style={{ textAlign: 'center', color: '#9A93A4', fontSize: 14, padding: '32px 20px' }}>Carregando amigos...</p>
          ) : amigos.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#9A93A4', fontSize: 14, padding: '32px 20px' }}>Nenhum amigo disponível para convidar.</p>
          ) : amigos.map(a => {
            const sel = selecionados.has(a.id);
            return (
              <button key={a.id} onClick={() => onToggle(a.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 20px', background: sel ? '#F7F5FF' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 38, height: 38, borderRadius: 12, overflow: 'hidden', background: '#EDE9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {a.foto_url
                    ? <img src={a.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontSize: 16, fontWeight: 700, color: '#5B3CE4' }}>{(a.nome || '?').charAt(0).toUpperCase()}</span>
                  }
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#211C2B', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.nome || a.username || 'Usuário'}</p>
                  {a.username && <p style={{ fontSize: 12, color: '#9A93A4', margin: '1px 0 0' }}>@{a.username}</p>}
                </div>
                <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${sel ? '#5B3CE4' : '#D4CFE2'}`, background: sel ? '#5B3CE4' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {sel && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
              </button>
            );
          })}
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid #F2EEF9' }}>
          <button onClick={onEnviar} disabled={enviando || selecionados.size === 0}
            style={{ width: '100%', padding: '14px', borderRadius: 14, background: selecionados.size === 0 ? '#D4CFE2' : '#5B3CE4', color: '#fff', fontSize: 15, fontWeight: 700, border: 'none', cursor: selecionados.size === 0 ? 'default' : 'pointer' }}>
            {enviando ? 'Enviando...' : selecionados.size === 0 ? 'Selecione amigos' : `Enviar convite${selecionados.size > 1 ? 's' : ''} (${selecionados.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Clubes;
